import React, { useState, useEffect, useCallback, useRef } from "react";
import useDarkMode from '../../hooks/useDarkMode';
import useThemeColor from "../../hooks/useThemeColor";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../../config/axiosApi/axiosApi";
import APIs from "../../Constants/apis";
import GridSectionLoader from "../../components/CustomComponents/preLoad/gridSectionLoader";
import FormInput from "../../components/CustomComponents/Input/formInput";
import ToggleSwitch from "../../components/CustomComponents/Switchery/switchery";
import Select from "react-select";
import DropDownStyles, { DropDownStylesDark } from "../staff/dropdownStyles";
import showErrorToast, {
  showSuccessToast,
} from "../../components/CustomComponents/toast/toast";
import SubscriptionModal from "./subscriptionModal";
import PaymentMethodModal from "./paymentMethodModal";
import DeliveryChargeRules from "./DeliveryChargeRules";
import PrintingReceipts from "../printing Receipts/printingReceipts";
import ScheduleManager from "../../components/CustomComponents/TimeSchedule/weeklySlots";
import schedule from "../../components/CustomComponents/TimeSchedule/schedule";
import moment from "moment";
import { updateUserData } from "../../app/auth/authSlice";
import { useProfileAndSettings } from "../../hooks/useProfileAndSettings";
import { getCurrencySymbol } from "../../utils/currencyDisplay";
import CollectionQrSection from "../../components/CustomComponents/CollectionQr/CollectionQrSection";
import "./settingsPage.css";

const SETTINGS_TABS = [
  { id: "charges", label: "Charges", icon: "fas fa-money-bill-wave" },
  { id: "vat", label: "VAT", icon: "fas fa-receipt" },
  { id: "reservation", label: "Reservation", icon: "fas fa-calendar-check" },
  { id: "businessTime", label: "Business Time", icon: "fas fa-clock" },
  { id: "onlineOrdering", label: "Online Ordering", icon: "fas fa-shopping-cart" },
  // Hidden for now — pre-ordering UI moved here from Online Ordering.
  { id: "preOrdering", label: "Pre-Ordering", icon: "fas fa-calendar-plus", hidden: true },
  { id: "printingReceipts", label: "Printing Receipts", icon: "fas fa-print" },
];

const TIME_PARSE_FORMATS = [
  "h:mm A",
  "h:mmA",
  "hh:mm A",
  "hh:mmA",
  "HH:mm:ss",
  "HH:mm",
  "HH:mm:ss.SSSSSSS",
];

/** Normalize API/DB time strings to HH:mm for pickers and saves. */
const normalizeTimeToHHmm = (time) => {
  if (time === null || time === undefined || time === "") return null;
  if (moment.isMoment(time)) {
    return time.isValid() ? time.format("HH:mm") : null;
  }
  const raw = String(time).trim();
  if (!raw) return null;

  // Prefer 12h formats first so "12:00 AM" is midnight, not noon via loose HH:mm.
  const parsed = moment(raw, TIME_PARSE_FORMATS, true);
  if (parsed.isValid()) return parsed.format("HH:mm");

  // DateTime-style values from SQL (e.g. "1900-01-01T09:00:00")
  const iso = moment(raw, moment.ISO_8601, true);
  if (iso.isValid()) return iso.format("HH:mm");

  const loose12 = moment(raw, ["h:mm A", "h:mmA", "hh:mm A", "hh:mmA"], true);
  if (loose12.isValid()) return loose12.format("HH:mm");

  const loose24 = moment(raw, ["HH:mm:ss", "HH:mm"], true);
  if (loose24.isValid()) return loose24.format("HH:mm");

  return null;
};

const getTimingId = (timing) =>
  Number(timing?.aT_ID ?? timing?.AT_ID ?? timing?.at_ID ?? 0) || 0;

const getTimingDayValue = (timing, day, kind) =>
  timing?.[`aT_${day}${kind}`] ??
  timing?.[`AT_${day}${kind}`] ??
  timing?.[`at_${day}${kind}`] ??
  null;

const getTimingType = (timing) =>
  String(timing?.type ?? timing?.Type ?? "").trim();

/** Restaurant rows were historically stored as "Resturent". */
const isTimingTypeMatch = (timingType, expectedType) => {
  const actual = String(timingType || "").toLowerCase();
  const expected = String(expectedType || "").toLowerCase();
  if (actual === expected) return true;
  if (expected === "restaurant") {
    return actual === "restaurant" || actual === "resturent";
  }
  return false;
};

const timingHasAnyHours = (timing) => {
  if (!timing) return false;
  const days = [
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
    "Sunday",
  ];
  return days.some((day) =>
    normalizeTimeToHHmm(getTimingDayValue(timing, day, "Opening"))
  );
};

/**
 * Prefer the row that matches the channel type and actually has hours.
 * Keeps legacy "Resturent" rows working and avoids stale duplicate rows.
 */
const pickPrimaryTiming = (timings = [], preferredType = "") => {
  if (!Array.isArray(timings) || timings.length === 0) return null;

  const matched = preferredType
    ? timings.filter((t) => isTimingTypeMatch(getTimingType(t), preferredType))
    : timings;
  const pool = matched.length ? matched : timings;
  const withHours = pool.filter(timingHasAnyHours);
  const ranked = (withHours.length ? withHours : pool).sort((a, b) => {
    const typeA = getTimingType(a).toLowerCase();
    const typeB = getTimingType(b).toLowerCase();
    const preferred = String(preferredType || "").toLowerCase();
    // Exact type wins over legacy alias when both exist.
    if (preferred) {
      if (typeA === preferred && typeB !== preferred) return -1;
      if (typeB === preferred && typeA !== preferred) return 1;
    }
    return getTimingId(b) - getTimingId(a);
  });
  return ranked[0] || null;
};

const convertToScheduleFormatForProfile = (timings, preferredType = "") => {
  const days = [
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
    "Sunday",
  ];
  const firstTiming = pickPrimaryTiming(timings, preferredType) || {};

  // Check if any day has actual timing data
  const hasAnyTiming = days.some((day) => {
    const opening = normalizeTimeToHHmm(getTimingDayValue(firstTiming, day, "Opening"));
    const closing = normalizeTimeToHHmm(getTimingDayValue(firstTiming, day, "Closing"));
    return opening && closing;
  });

  // If no timing data exists, return null to indicate empty schedule
  if (!hasAnyTiming) {
    return null;
  }

  const timingType = getTimingType(firstTiming);

  return days.map((day) => {
    const opening = normalizeTimeToHHmm(getTimingDayValue(firstTiming, day, "Opening"));
    const closing = normalizeTimeToHHmm(getTimingDayValue(firstTiming, day, "Closing"));
    const checked = !!(opening && closing);
    return {
      day,
      checked,
      timeSlots: checked
        ? [
            {
              from: opening,
              to: closing,
              description:
                isTimingTypeMatch(timingType, "Restaurant")
                  ? "All Day"
                  : timingType || "",
            },
          ]
        : [{ from: "00:00", to: "23:59" }],
    };
  });
};

const formatTimeForDisplay = (time) => {
  if (!time) return "";
  const normalized = normalizeTimeToHHmm(time);
  if (!normalized) return "";
  return moment(normalized, "HH:mm").format("h:mm A");
};

/** Preserves tbl_Settings.DeliveryCharges on API saves; the control is hidden in the UI. */
const deliveryChargesForSettingsPayload = (value) => {
  const dc = value;
  if (dc === undefined || dc === null) return "";
  if (typeof dc === "number") return dc.toFixed(2);
  if (typeof dc !== "string") return String(dc);
  return dc;
};

/** Persists delivery charge settings — always postcode rules (fixed mode removed). */
const deliveryChargeSettingsPayload = () => ({
  UseFixedDeliveryCharge: false,
  DeliveryCharges: deliveryChargesForSettingsPayload(0),
});

// Ensure numeric fields are numbers or null (not empty strings).
const sanitizeSettingsPayload = (state) => {
  const numericFields = [
    "S_ServiceCharges",
    "S_VAT",
    "S_ServicesChargesOnCovers",
    "S_MinOrderAmount",
    "S_DeliveryTime",
    "S_TableReservationMarginTime",
    "S_TableReservedRelesaeTime",
    "S_ShowingReserveredTableTime",
    "MaximumReservationDuration",
    "S_ShowCancelledOrderDuration",
    "S_VisibleCancelOrderDaysinCancelTab",
    "PreOrderAlertInMins",
  ];

  const sanitized = { ...state };

  numericFields.forEach((field) => {
    const value = sanitized[field];
    if (value === "" || value === undefined) {
      sanitized[field] = null; // backend expects nullable ints
    } else if (typeof value === "string") {
      const parsed = Number(value);
      sanitized[field] = Number.isNaN(parsed) ? null : parsed;
    }
  });

  // Special handling for S_DeliveryTime to ensure it's always an integer
  if (sanitized.S_DeliveryTime !== null && sanitized.S_DeliveryTime !== undefined) {
    sanitized.S_DeliveryTime = parseInt(sanitized.S_DeliveryTime) || 0;
  }

  // Special handling for VAT when tax is disabled
  if (!sanitized.IsTaxEnabled) {
    sanitized.S_VAT = null; // or 0, depending on your backend requirements
    sanitized.S_VATRegNo = ""; // empty string for text field
    sanitized.S_VARType = "Inclusive"; // default value
  }

  // Keep DeliveryCharges on tbl_Settings in sync (field hidden in UI).
  const dc = sanitized.DeliveryCharges;
  if (dc === undefined || dc === null) {
    sanitized.DeliveryCharges = "";
  } else if (typeof dc === "number") {
    sanitized.DeliveryCharges = dc.toFixed(2);
  } else if (typeof dc !== "string") {
    sanitized.DeliveryCharges = String(dc);
  }

  return sanitized;
};

/** First non-empty string among API camelCase / PascalCase aliases. */
const pickSettingsString = (...values) => {
  for (const value of values) {
    if (value == null) continue;
    const text = String(value).trim();
    if (text) return text;
  }
  return "";
};

function Settings() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const CompID = useSelector((state) => state.auth.user)?.userData?.compID;
  const { settingsData } = useSelector((state) => state.auth);
  
  // Get app settings from Redux
  const appSettings = useSelector((state) => state.appSettings);
  const { appTheme } = appSettings;
  
  // Check if dark mode is enabled
  const isDarkMode = useDarkMode();
  const dropdownStyles = isDarkMode ? DropDownStylesDark : DropDownStyles;

  // Get theme color from centralized hook
  const themeColor = useThemeColor();

  const settingsThemeStyle = {
    "--settings-accent": themeColor,
    "--settings-accent-soft": `${themeColor}24`,
    "--settings-accent-shadow": `${themeColor}40`,
  };

  const renderSectionHeading = (title, subtitle) => (
    <div className="settings-section-heading">
      <div className="d-flex align-items-start gap-2">
        <span className="settings-section-heading__bar" aria-hidden />
        <div className="min-w-0">
          <h2 className="settings-section-heading__title h6">{title}</h2>
          {subtitle ? (
            <p className="settings-section-heading__subtitle">{subtitle}</p>
          ) : null}
        </div>
      </div>
    </div>
  );

  const { getCompCurrency } = useProfileAndSettings();
  const currencySymbol = getCurrencySymbol(getCompCurrency());

  const [loading, setLoading] = useState(false);
  const [editingField, setEditingField] = useState(null);
  const [editValue, setEditValue] = useState("");
  const [taxEditing, setTaxEditing] = useState(false);
  const [previousSettingsData, setPreviousSettingsData] = useState(null);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [isSubscriptionModalOpen, setIsSubscriptionModalOpen] = useState(false);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [paymentMethodToEdit, setPaymentMethodToEdit] = useState(null);
  const [isOnlineOrderingEditing, setIsOnlineOrderingEditing] = useState(false);
  const [onlineOrderingPreviousData, setOnlineOrderingPreviousData] = useState(null);
  const [isPreOrderingEditing, setIsPreOrderingEditing] = useState(false);
  const [preOrderingPreviousData, setPreOrderingPreviousData] = useState(null);
  const [isGeneralEditing, setIsGeneralEditing] = useState(false);
  const [generalPreviousData, setGeneralPreviousData] = useState(null);
  const [isTableReservationEditing, setIsTableReservationEditing] = useState(false);
  const [tableReservationPreviousData, setTableReservationPreviousData] = useState(null);
  const [isKitchenEditing, setIsKitchenEditing] = useState(false);
  const [kitchenPreviousData, setKitchenPreviousData] = useState(null);
  const [isTVAppEditing, setIsTVAppEditing] = useState(false);
  const [tvAppPreviousData, setTVAppPreviousData] = useState(null);
  const [enableSchedule, setEnableSchedule] = useState({
    weeklyServiceHours: false,
    DeliveryTime: false,
    CollectionTime: false,
  });
  const [schedules, setSchedules] = useState({
    weeklyServiceHours: schedule.map((d) => ({
      ...d,
      timeSlots: (d.timeSlots || []).map((s) => ({ ...s })),
    })),
    DeliveryTime: schedule.map((d) => ({
      ...d,
      timeSlots: (d.timeSlots || []).map((s) => ({ ...s })),
    })),
    CollectionTime: schedule.map((d) => ({
      ...d,
      timeSlots: (d.timeSlots || []).map((s) => ({ ...s })),
    })),
  });
  const [timingData, setTimingData] = useState({
    weeklyServiceHours: [],
    DeliveryTime: [],
    CollectionTime: [],
  });
  const schedulesRef = useRef(schedules);
  const enableScheduleRef = useRef(enableSchedule);
  const timingDataRef = useRef(timingData);
  useEffect(() => {
    schedulesRef.current = schedules;
  }, [schedules]);
  useEffect(() => {
    enableScheduleRef.current = enableSchedule;
  }, [enableSchedule]);
  useEffect(() => {
    timingDataRef.current = timingData;
  }, [timingData]);
  const [activeSettingsTab, setActiveSettingsTab] = useState("charges");
  const [useFixedDeliveryCharge, setUseFixedDeliveryCharge] = useState(false);
  const [formState, setFormState] = useState({
    s_ID: 0,
    CompID: CompID,
    s_VAT: "",
    s_VATRegNo: "",
    s_VARType: "inclusive",
    s_ServiceCharges: 0,
    deliveryCharges: "",
    useFixedDeliveryCharge: false,
    s_ServicesChargesOnCovers: "",
    s_MinOrderAmount: "",
    s_DeliveryTime: 0,
    s_ReturnPolicy: "",
    isShowWarningOnDeletingCustomerData: false,
    s_IsUpdatefromKitchen: true,
    manageCoversOnTablePlan: true,
    s_TableReservationMarginTime: 0,
    s_TableReservedReleaseTime: 0,
    s_ShowingReserveredTableTime: 0,
    maximumReservationDuration: 0,
    s_ShowCancelledOrderDuration: 0,
    s_VisibleCancelOrderDaysinCancelTab: 0,
    tV_ISOrderStatus: false,
    tV_ISOrderType: false,
    tV_ISOrderNumber: false,
    tV_ISOrderOwner: false,
    isActivePreOrderModule: false,
    preOrderingCollection: false,
    preOrderingDelivery: false,
    preOrderDayRange: 0,
    preOrderAlertInMins: 20,
    compDeliveryStatus: false,
    compCollectionStatus: false,
    s_OnlineOrderingEmail: "",
    s_OnlineOrderingPhone: "",
    s_OnlineOrderingCollectionPayment: true,
    isTaxEnabled: false,
    SubscriptionPlan: "ServeIQ for Restaurant Free",
    LocationCount: 1,
    KioskCount: 0,
    SubscriptionEndDate: "29 March 2025",
    SubscriptionPlanName: "ServeIQ for Restaurant Plus",
  });

  const preOrderOptions = [
    { value: null, label: "Select Limit" },
    { value: 0, label: "No Limit" },
    { value: 1, label: "1 Day" },
    { value: 2, label: "2 Days" },
    { value: 3, label: "3 Days" },
    { value: 4, label: "4 Days" },
    { value: 5, label: "5 Days" },
    { value: 6, label: "6 Days" },
    { value: 7, label: "7 Days" },
    { value: 8, label: "8 Days" },
    { value: 9, label: "9 Days" },
    { value: 10, label: "10 Days" },
    { value: 11, label: "11 Days" },
    { value: 12, label: "12 Days" },
    { value: 13, label: "13 Days" },
    { value: 14, label: "14 Days" },
    { value: 15, label: "15 Days" },
    { value: 16, label: "16 Days" },
    { value: 17, label: "17 Days" },
    { value: 18, label: "18 Days" },
    { value: 19, label: "19 Days" },
    { value: 20, label: "20 Days" },
    { value: 21, label: "21 Days" },
    { value: 22, label: "22 Days" },
    { value: 23, label: "23 Days" },
    { value: 24, label: "24 Days" },
    { value: 25, label: "25 Days" },
    { value: 26, label: "26 Days" },
    { value: 27, label: "27 Days" },
    { value: 28, label: "28 Days" },
    { value: 29, label: "29 Days" },
    { value: 30, label: "30 Days" },
  ];

  const fetchSettings = useCallback(async () => {
    try {
      setLoading(true);
      // Fetch fresh data from API
      const response = await axiosInstance.get(`${APIs.GET_COMPANY_SETTINGS}`);
      const data = response.data.data;
      
      // Update Redux state with fresh data
      dispatch(updateUserData({
        settingsData: data,
        lastDataRefresh: new Date().toISOString()
      }));

      // Handle timing data
      const timingFields = [
        "weeklyServiceHours",
        "DeliveryTime",
        "CollectionTime",
      ];
      const timingMap = {
        weeklyServiceHours: "Restaurant",
        DeliveryTime: "Delivery",
        CollectionTime: "Collection",
      };

      const newTimingData = {};
      timingFields.forEach((field) => {
        const preferredType = timingMap[field];
        const filteredTimings =
          data.timings?.filter((t) =>
            isTimingTypeMatch(getTimingType(t), preferredType)
          ) || [];
        const primaryTiming = pickPrimaryTiming(filteredTimings, preferredType);
        newTimingData[field] = primaryTiming ? [primaryTiming] : [];
        if (primaryTiming) {
          const convertedSchedules = convertToScheduleFormatForProfile(
            [primaryTiming],
            preferredType
          );
          if (convertedSchedules) {
            setEnableSchedule((prev) => ({ ...prev, [field]: true }));
            setSchedules((prev) => ({ ...prev, [field]: convertedSchedules }));
          } else {
            setEnableSchedule((prev) => ({ ...prev, [field]: false }));
            setSchedules((prev) => ({ ...prev, [field]: schedule }));
          }
        } else {
          setEnableSchedule((prev) => ({ ...prev, [field]: false }));
          setSchedules((prev) => ({ ...prev, [field]: schedule }));
        }
      });

      setTimingData(newTimingData);

      // Fixed delivery mode is retired — always use postcode rules.
      setUseFixedDeliveryCharge(false);

      setFormState((prev) => ({
        ...prev,
        ...data,
        s_ID: data.s_ID,
        CompID: data.CompID || data.compID || CompID,
        s_VAT: data.s_VAT || "",
        s_VATRegNo: data.s_VATRegNo || "",
        s_VARType: data.s_VARType || "inclusive",
        s_ServiceCharges: data.s_ServiceCharges || 0,
        deliveryCharges: data.deliveryCharges ?? data.DeliveryCharges ?? "",
        useFixedDeliveryCharge: false,
        s_ServicesChargesOnCovers: data.s_ServicesChargesOnCovers || "",
        s_MinOrderAmount: data.s_MinOrderAmount || "",
        s_DeliveryTime: data.s_DeliveryTime || 0,
        s_ReturnPolicy: data.s_ReturnPolicy || "",
        isShowWarningOnDeletingCustomerData:
          !!data.isShowWarningOnDeletingCustomerData,
        s_IsUpdatefromKitchen: !!data.s_IsUpdatefromKitchen,
        manageCoversOnTablePlan: !!data.manageCoversOnTablePlan,
        s_TableReservationMarginTime: data.s_TableReservationMarginTime || 0,
        s_TableReservedReleaseTime:
          data.s_TableReservedRelesaeTime ??
          data.S_TableReservedRelesaeTime ??
          data.s_TableReservedReleaseTime ??
          0,
        s_ShowingReserveredTableTime: data.s_ShowingReserveredTableTime || 0,
        maximumReservationDuration: data.maximumReservationDuration || data.MaximumReservationDuration || 0,
        s_ShowCancelledOrderDuration: data.s_ShowCancelledOrderDuration || 0,
        s_VisibleCancelOrderDaysinCancelTab:
          data.s_VisibleCancelOrderDaysinCancelTab || 0,
        tV_ISOrderStatus: !!data.tV_ISOrderStatus,
        tV_ISOrderType: !!data.tV_ISOrderType,
        tV_ISOrderNumber: !!data.tV_ISOrderNumber,
        tV_ISOrderOwner: !!data.tV_ISOrderOwner,
        isActivePreOrderModule: !!data.isActivePreOrderModule,
        preOrderingCollection: !!data.preOrderingCollection,
        preOrderingDelivery: !!data.preOrderingDelivery,
        preOrderDayRange: data.preOrderDayRange || 0,
        preOrderAlertInMins: data.preOrderAlertInMins || 20,
        compDeliveryStatus: !!data.compDeliveryStatus,
        compCollectionStatus: !!data.compCollectionStatus,
        s_OnlineOrderingEmail: pickSettingsString(
          data.s_OnlineOrderingEmail,
          data.S_OnlineOrderingEmail,
          data.onlineOrderingEmail
        ),
        s_OnlineOrderingPhone: pickSettingsString(
          data.s_OnlineOrderingPhone,
          data.S_OnlineOrderingPhone,
          data.onlineOrderingPhone
        ),
        s_OnlineOrderingCollectionPayment:
          data.s_OnlineOrderingCollectionPayment ??
          data.S_OnlineOrderingCollectionPayment ??
          true,
        isTaxEnabled: data.isTaxEnabled ?? false,
        SubscriptionPlan:
          data.SubscriptionPlan,
        LocationCount: data.LocationCount || 1,
        KioskCount: data.KioskCount || 0,
        SubscriptionEndDate: data.SubscriptionEndDate,
        SubscriptionPlanName:
          data.SubscriptionPlanName,
      }));
    } catch (err) {
      showErrorToast("Failed to load settings");
    } finally {
      setLoading(false);
    }
  }, [dispatch]);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  useEffect(() => {
    const getElements = () => ({
      sections: document.querySelectorAll("#bsSpyContent > div"),
      navLinks: document.querySelectorAll("#bsSpyTarget > a"),
    });

    let { sections, navLinks } = getElements();

    if (!sections.length || !navLinks.length) {
      return;
    }

    function activateNavLink(id) {
      navLinks.forEach((link) => {
        link.classList.remove("active");
        // Reset to inactive styling
        link.style.color = isDarkMode ? '#d1d5db' : '#6b7280';
        link.style.borderLeft = '3px solid transparent';
      });
      const target = document.querySelector(`#bsSpyTarget a[href="#${id}"]`);
      if (target) {
        target.classList.add("active");
        // Apply active styling with theme color
        target.style.color = themeColor;
        target.style.borderLeft = `3px solid ${themeColor}`;
      }
    }

    function handleScroll() {
      sections = getElements().sections;

      let activeSectionId = null;
      const scrollPosition = window.scrollY + 100; // Offset for better detection
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;

      // Check if we're near the bottom of the page
      const isNearBottom = (window.scrollY + windowHeight) >= (documentHeight - 50);

      // Find the section that's currently in view
      for (let i = sections.length - 1; i >= 0; i--) {
        const section = sections[i];
        const rect = section.getBoundingClientRect();
        const sectionTop = section.offsetTop;
        const sectionHeight = section.offsetHeight;
        
        // Check if the scroll position is within this section
        if (scrollPosition >= sectionTop && scrollPosition < sectionTop + sectionHeight) {
          activeSectionId = section.getAttribute("id");
          break;
        }
      }

      // If no section is found, check if we're at the top
      if (!activeSectionId && window.scrollY < 100) {
        activeSectionId = sections[0]?.getAttribute("id");
      }

      // If we're near the bottom of the page, highlight the last section
      if (!activeSectionId && isNearBottom) {
        activeSectionId = sections[sections.length - 1]?.getAttribute("id");
      }

      // Additional check: if we're at the very bottom, ensure last section is highlighted
      if (!activeSectionId) {
        const lastSection = sections[sections.length - 1];
        if (lastSection) {
          const lastRect = lastSection.getBoundingClientRect();
          // If the last section is visible or we're past it, highlight it
          if (lastRect.top <= windowHeight || window.scrollY + windowHeight >= documentHeight - 10) {
            activeSectionId = lastSection.getAttribute("id");
          }
        }
      }

      if (activeSectionId) {
        activateNavLink(activeSectionId);
      }
    }

    let scrollTimeout;
    const throttleScroll = () => {
      if (!scrollTimeout) {
        scrollTimeout = setTimeout(() => {
          handleScroll();
          scrollTimeout = null;
        }, 10); // Reduced timeout for more responsive updates
      }
    };

    // Add immediate scroll handler for better responsiveness
    window.addEventListener("scroll", handleScroll);
    window.addEventListener("scroll", throttleScroll);
    handleScroll();

    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("scroll", throttleScroll);
      clearTimeout(scrollTimeout);
    };
  }, []);

  const handleInputChange = useCallback((e, isDropdown = false) => {
    const value = isDropdown
      ? e.value
      : e.target.type === "checkbox"
        ? e.target.checked
        : e.target.value;
    const name = isDropdown ? e.name : e.target.name;

    // Handle checkbox values properly
    if (e.target.type === "checkbox") {
      setFormState((prev) => ({
        ...prev,
        [name]: e.target.checked,
      }));
      return;
    }

    const sanitizedValue = [
      "s_ServiceCharges",
      "s_VAT",
      "s_ServicesChargesOnCovers",
      "s_MinOrderAmount",
      "s_DeliveryTime",
      "s_TableReservationMarginTime",
      "s_TableReservedReleaseTime",
      "s_ShowingReserveredTableTime",
      "maximumReservationDuration",
      "s_ShowCancelledOrderDuration",
      "s_VisibleCancelOrderDaysinCancelTab",
      "preOrderAlertInMins",
    ].includes(name)
      ? isNaN(Number(value)) || value === ""
        ? ""
        : Number(value)
      : value;

    if (typeof sanitizedValue === "number" && sanitizedValue < 0) {
      showErrorToast(
        `${name.replace(/([A-Z])/g, " $1").trim()} must be a positive number`
      );
      return;
    }

    setEditValue(sanitizedValue);
    setFormState((prev) => ({
      ...prev,
      [name]: sanitizedValue,
    }));
  }, []);

  const handleSelectChange = (name, option) => {
    handleInputChange({ name, value: option ? option.value : "" }, true);
  };

  const handleToggleChange = async (name, checked) => {
    try {
      await saveField(name, checked);
    } catch (error) {
      showErrorToast(`Failed to save ${name}`);
    }
  };

  const startEditing = (name, value) => {
    if (name === "Tax") {
      setTaxEditing(true);
      setPreviousSettingsData({ ...formState });
    } else {
      setEditingField(name);
      setEditValue(value);
      setPreviousSettingsData({ ...formState });

      // Handle timing fields
      if (
        ["weeklyServiceHours", "DeliveryTime", "CollectionTime"].includes(name)
      ) {
        const hasTimingData = timingHasAnyHours(value?.[0] || value);
        setEnableSchedule((prev) => ({ ...prev, [name]: hasTimingData }));
      }
    }
  };

  const cancelEditing = () => {
    if (previousSettingsData) {
      setFormState(previousSettingsData);
    }
    setEditingField(null);
    setTaxEditing(false);
    setEditValue("");
    setPreviousSettingsData(null);
  };

  const cancelEditingAfterSubmit = () => {
    setEditingField(null);
    setTaxEditing(false);
    setEditValue("");
    setPreviousSettingsData(null);
  };

  const startOnlineOrderingEditing = () => {
    setIsOnlineOrderingEditing(true);
    setOnlineOrderingPreviousData({
      compDeliveryStatus: formState.compDeliveryStatus,
      compCollectionStatus: formState.compCollectionStatus,
      s_MinOrderAmount: formState.s_MinOrderAmount,
      s_DeliveryTime: formState.s_DeliveryTime,
      s_OnlineOrderingEmail: formState.s_OnlineOrderingEmail,
      s_OnlineOrderingPhone: formState.s_OnlineOrderingPhone,
      s_OnlineOrderingCollectionPayment: formState.s_OnlineOrderingCollectionPayment,
    });
  };

  const cancelOnlineOrderingEditing = () => {
    if (onlineOrderingPreviousData) {
      setFormState((prev) => ({
        ...prev,
        ...onlineOrderingPreviousData,
      }));
    }
    setIsOnlineOrderingEditing(false);
    setOnlineOrderingPreviousData(null);
  };

  const startPreOrderingEditing = () => {
    setIsPreOrderingEditing(true);
    setPreOrderingPreviousData({
      isActivePreOrderModule: formState.isActivePreOrderModule,
      preOrderingCollection: formState.preOrderingCollection,
      preOrderingDelivery: formState.preOrderingDelivery,
      preOrderDayRange: formState.preOrderDayRange,
      preOrderAlertInMins: formState.preOrderAlertInMins,
    });
  };

  const cancelPreOrderingEditing = () => {
    if (preOrderingPreviousData) {
      setFormState((prev) => ({
        ...prev,
        ...preOrderingPreviousData,
      }));
    }
    setIsPreOrderingEditing(false);
    setPreOrderingPreviousData(null);
  };

  const startGeneralEditing = () => {
    setIsGeneralEditing(true);
    setGeneralPreviousData({
      s_ServiceCharges: formState.s_ServiceCharges,
      s_MinOrderAmount: formState.s_MinOrderAmount,
      s_DeliveryTime: formState.s_DeliveryTime,
      s_ReturnPolicy: formState.s_ReturnPolicy,
      isShowWarningOnDeletingCustomerData: formState.isShowWarningOnDeletingCustomerData,
      manageCoversOnTablePlan: formState.manageCoversOnTablePlan,
      deliveryCharges: formState.deliveryCharges,
      useFixedDeliveryCharge: useFixedDeliveryCharge,
    });
  };

  const cancelGeneralEditing = () => {
    if (generalPreviousData) {
      setFormState((prev) => ({
        ...prev,
        ...generalPreviousData,
      }));
      if (generalPreviousData.useFixedDeliveryCharge !== undefined) {
        setUseFixedDeliveryCharge(generalPreviousData.useFixedDeliveryCharge);
      }
    }
    setIsGeneralEditing(false);
    setGeneralPreviousData(null);
  };

  const startTableReservationEditing = () => {
    setIsTableReservationEditing(true);
    setTableReservationPreviousData({
      s_TableReservationMarginTime: formState.s_TableReservationMarginTime,
      s_TableReservedReleaseTime: formState.s_TableReservedReleaseTime,
      s_ShowingReserveredTableTime: formState.s_ShowingReserveredTableTime,
      maximumReservationDuration: formState.maximumReservationDuration,
    });
  };

  const cancelTableReservationEditing = () => {
    if (tableReservationPreviousData) {
      setFormState((prev) => ({
        ...prev,
        ...tableReservationPreviousData,
      }));
    }
    setIsTableReservationEditing(false);
    setTableReservationPreviousData(null);
  };

  const saveTableReservationSettings = async () => {
    try {
      setLoading(true);

      const updatedSettings = {
        S_ID: formState.s_ID || 0,
        CompID: formState.CompID,
        // Update only Table Reservation fields
        S_TableReservationMarginTime: parseInt(formState.s_TableReservationMarginTime) || 0,
        S_TableReservedRelesaeTime: parseInt(formState.s_TableReservedReleaseTime) || 0,
        S_ShowingReserveredTableTime: parseInt(formState.s_ShowingReserveredTableTime) || 0,
        MaximumReservationDuration: parseInt(formState.maximumReservationDuration) || 0,
        // Keep all other fields unchanged
        S_ServiceCharges: parseInt(formState.s_ServiceCharges) || 0,
        ...deliveryChargeSettingsPayload(),
        S_MinOrderAmount: parseFloat(formState.s_MinOrderAmount) || 0,
        S_DeliveryTime: parseInt(formState.s_DeliveryTime) || 0,
        S_ReturnPolicy: formState.s_ReturnPolicy || "",
        IsShowWarningOnDeletingCustomerData: Boolean(formState.isShowWarningOnDeletingCustomerData),
        ManageCoversOnTablePlan: Boolean(formState.manageCoversOnTablePlan),
        S_VAT: formState.s_VAT || "",
        S_VATRegNo: formState.s_VATRegNo || "",
        S_VARType: formState.s_VARType || "inclusive",
        IsTaxEnabled: Boolean(formState.isTaxEnabled),
        S_IsUpdatefromKitchen: Boolean(formState.s_IsUpdatefromKitchen),
        S_ShowCancelledOrderDuration: parseInt(formState.s_ShowCancelledOrderDuration) || 0,
        S_VisibleCancelOrderDaysinCancelTab: parseInt(formState.s_VisibleCancelOrderDaysinCancelTab) || 0,
        TV_ISOrderStatus: Boolean(formState.tV_ISOrderStatus),
        TV_ISOrderType: Boolean(formState.tV_ISOrderType),
        TV_ISOrderNumber: Boolean(formState.tV_ISOrderNumber),
        TV_ISOrderOwner: Boolean(formState.tV_ISOrderOwner),
        IsActivePreOrderModule: Boolean(formState.isActivePreOrderModule),
        PreOrderingCollection: Boolean(formState.preOrderingCollection),
        PreOrderingDelivery: Boolean(formState.preOrderingDelivery),
        PreOrderDayRange: parseInt(formState.preOrderDayRange) || 0,
        PreOrderAlertInMins: parseInt(formState.preOrderAlertInMins) || 20,
        CompDeliveryStatus: Boolean(formState.compDeliveryStatus),
        CompCollectionStatus: Boolean(formState.compCollectionStatus),
      };


      const response = await axiosInstance.post(
        APIs.POST_UPDATE_COMPANY_SETTING,
        updatedSettings,
        {
          headers: {
            'Content-Type': 'application/json',
          }
        }
      );

      if (response.data.status === true) {
        showSuccessToast("Table Reservation settings updated successfully");
        setIsTableReservationEditing(false);
        setTableReservationPreviousData(null);
        await fetchSettings();
      } else {
        const errorMessage = response.data?.Message || response.data?.message || "Failed to update Table Reservation settings";
        showErrorToast(errorMessage);
      }
    } catch (error) {
      console.error("Error in saveTableReservationSettings:", error);
      showErrorToast(
        error.response?.data?.Message ||
        error.response?.data?.message ||
        "Failed to update Table Reservation settings"
      );
    } finally {
      setLoading(false);
    }
  };

  const startKitchenEditing = () => {
    setIsKitchenEditing(true);
    setKitchenPreviousData({
      s_IsUpdatefromKitchen: formState.s_IsUpdatefromKitchen,
      s_ShowCancelledOrderDuration: formState.s_ShowCancelledOrderDuration,
      s_VisibleCancelOrderDaysinCancelTab: formState.s_VisibleCancelOrderDaysinCancelTab,
    });
  };

  const cancelKitchenEditing = () => {
    if (kitchenPreviousData) {
      setFormState((prev) => ({
        ...prev,
        ...kitchenPreviousData,
      }));
    }
    setIsKitchenEditing(false);
    setKitchenPreviousData(null);
  };

  const saveKitchenSettings = async () => {
    try {
      setLoading(true);

      const updatedSettings = {
        S_ID: formState.s_ID || 0,
        CompID: formState.CompID,
        // Update only Kitchen fields
        S_IsUpdatefromKitchen: Boolean(formState.s_IsUpdatefromKitchen),
        S_ShowCancelledOrderDuration: parseInt(formState.s_ShowCancelledOrderDuration) || 0,
        S_VisibleCancelOrderDaysinCancelTab: parseInt(formState.s_VisibleCancelOrderDaysinCancelTab) || 0,
        // Keep all other fields unchanged
        S_ServiceCharges: parseInt(formState.s_ServiceCharges) || 0,
        ...deliveryChargeSettingsPayload(),
        S_MinOrderAmount: parseFloat(formState.s_MinOrderAmount) || 0,
        S_DeliveryTime: parseInt(formState.s_DeliveryTime) || 0,
        S_ReturnPolicy: formState.s_ReturnPolicy || "",
        IsShowWarningOnDeletingCustomerData: Boolean(formState.isShowWarningOnDeletingCustomerData),
        ManageCoversOnTablePlan: Boolean(formState.manageCoversOnTablePlan),
        S_VAT: formState.s_VAT || 0,
        S_VATRegNo: formState.s_VATRegNo || "",
        S_VARType: formState.s_VARType || "inclusive",
        IsTaxEnabled: Boolean(formState.isTaxEnabled),
        S_TableReservationMarginTime: parseInt(formState.s_TableReservationMarginTime) || 0,
        S_TableReservedRelesaeTime: parseInt(formState.s_TableReservedReleaseTime) || 0,
        S_ShowingReserveredTableTime: parseInt(formState.s_ShowingReserveredTableTime) || 0,
        MaximumReservationDuration: parseInt(formState.maximumReservationDuration) || 0,
        TV_ISOrderStatus: Boolean(formState.tV_ISOrderStatus),
        TV_ISOrderType: Boolean(formState.tV_ISOrderType),
        TV_ISOrderNumber: Boolean(formState.tV_ISOrderNumber),
        TV_ISOrderOwner: Boolean(formState.tV_ISOrderOwner),
        IsActivePreOrderModule: Boolean(formState.isActivePreOrderModule),
        PreOrderingCollection: Boolean(formState.preOrderingCollection),
        PreOrderingDelivery: Boolean(formState.preOrderingDelivery),
        PreOrderDayRange: parseInt(formState.preOrderDayRange) || 0,
        PreOrderAlertInMins: parseInt(formState.preOrderAlertInMins) || 20,
        CompDeliveryStatus: Boolean(formState.compDeliveryStatus),
        CompCollectionStatus: Boolean(formState.compCollectionStatus),
      };

      const response = await axiosInstance.post(
        APIs.POST_UPDATE_COMPANY_SETTING,
        updatedSettings,
        {
          headers: {
            'Content-Type': 'application/json',
          }
        }
      );

      if (response.data.status === true) {
        showSuccessToast("Kitchen settings updated successfully");
        setIsKitchenEditing(false);
        setKitchenPreviousData(null);
      } else {
        const errorMessage = response.data?.Message || response.data?.message || "Failed to update Kitchen settings";
        showErrorToast(errorMessage);
      }
    } catch (error) {
      console.error("Error in saveKitchenSettings:", error);
      showErrorToast(
        error.response?.data?.Message ||
        error.response?.data?.message ||
        "Failed to update Kitchen settings"
      );
    } finally {
      setLoading(false);
    }
  };

  const startTVAppEditing = () => {
    setIsTVAppEditing(true);
    setTVAppPreviousData({
      tV_ISOrderStatus: formState.tV_ISOrderStatus,
      tV_ISOrderType: formState.tV_ISOrderType,
      tV_ISOrderNumber: formState.tV_ISOrderNumber,
      tV_ISOrderOwner: formState.tV_ISOrderOwner,
    });
  };

  const cancelTVAppEditing = () => {
    if (tvAppPreviousData) {
      setFormState((prev) => ({
        ...prev,
        ...tvAppPreviousData,
      }));
    }
    setIsTVAppEditing(false);
    setTVAppPreviousData(null);
  };

  const saveTVAppSettings = async () => {
    try {
      setLoading(true);

      const updatedSettings = {
        S_ID: formState.s_ID || 0,
        CompID: formState.CompID,
        // Update only TV App fields
        TV_ISOrderStatus: Boolean(formState.tV_ISOrderStatus),
        TV_ISOrderType: Boolean(formState.tV_ISOrderType),
        TV_ISOrderNumber: Boolean(formState.tV_ISOrderNumber),
        TV_ISOrderOwner: Boolean(formState.tV_ISOrderOwner),
        // Keep all other fields unchanged
        S_ServiceCharges: parseInt(formState.s_ServiceCharges) || 0,
        ...deliveryChargeSettingsPayload(),
        S_MinOrderAmount: parseFloat(formState.s_MinOrderAmount) || 0,
        S_DeliveryTime: parseInt(formState.s_DeliveryTime) || 0,
        S_ReturnPolicy: formState.s_ReturnPolicy || "",
        IsShowWarningOnDeletingCustomerData: Boolean(formState.isShowWarningOnDeletingCustomerData),
        ManageCoversOnTablePlan: Boolean(formState.manageCoversOnTablePlan),
        S_VAT: formState.s_VAT || "",
        S_VATRegNo: formState.s_VATRegNo || "",
        S_VARType: formState.s_VARType || "inclusive",
        IsTaxEnabled: Boolean(formState.isTaxEnabled),
        S_TableReservationMarginTime: parseInt(formState.s_TableReservationMarginTime) || 0,
        S_TableReservedRelesaeTime: parseInt(formState.s_TableReservedReleaseTime) || 0,
        S_ShowingReserveredTableTime: parseInt(formState.s_ShowingReserveredTableTime) || 0,
        MaximumReservationDuration: parseInt(formState.maximumReservationDuration) || 0,
        S_IsUpdatefromKitchen: Boolean(formState.s_IsUpdatefromKitchen),
        S_ShowCancelledOrderDuration: parseInt(formState.s_ShowCancelledOrderDuration) || 0,
        S_VisibleCancelOrderDaysinCancelTab: parseInt(formState.s_VisibleCancelOrderDaysinCancelTab) || 0,
        IsActivePreOrderModule: Boolean(formState.isActivePreOrderModule),
        PreOrderingCollection: Boolean(formState.preOrderingCollection),
        PreOrderingDelivery: Boolean(formState.preOrderingDelivery),
        PreOrderDayRange: parseInt(formState.preOrderDayRange) || 0,
        PreOrderAlertInMins: parseInt(formState.preOrderAlertInMins) || 20,
        CompDeliveryStatus: Boolean(formState.compDeliveryStatus),
        CompCollectionStatus: Boolean(formState.compCollectionStatus),
      };

    

      const response = await axiosInstance.post(
        APIs.POST_UPDATE_COMPANY_SETTING,
        updatedSettings,
        {
          headers: {
            'Content-Type': 'application/json',
          }
        }
      );

      if (response.data && response.data.status === true) {
        showSuccessToast("TV App settings updated successfully");
        setIsTVAppEditing(false);
        setTVAppPreviousData(null);
      } else {
        const errorMessage = response.data?.Message || response.data?.message || "Failed to update TV App settings";
        showErrorToast(errorMessage);
      }
    } catch (error) {
      console.error("Error in saveTVAppSettings:", error);
      showErrorToast(
        error.response?.data?.Message ||
        error.response?.data?.message ||
        "Failed to update TV App settings"
      );
    } finally {
      setLoading(false);
    }
  };

  const saveGeneralSettings = async () => {
    try {
      setLoading(true);

      // Create complete settings data with only General fields updated
      const updatedSettings = {
        S_ID: formState.s_ID || 0,
        CompID: formState.CompID,
        S_ServiceCharges: (() => {
          const sc = formState.s_ServiceCharges;
          if (sc === "" || sc === undefined || sc === null) {
            return null;
          }
          return parseInt(sc, 10);
        })(),
        ...deliveryChargeSettingsPayload(),
        S_MinOrderAmount: parseFloat(formState.s_MinOrderAmount) || 0,
        S_DeliveryTime: parseInt(formState.s_DeliveryTime) || 0,
        S_ReturnPolicy: formState.s_ReturnPolicy || "",
        IsShowWarningOnDeletingCustomerData: Boolean(formState.isShowWarningOnDeletingCustomerData),
        ManageCoversOnTablePlan: Boolean(formState.manageCoversOnTablePlan),
        // Keep all other fields unchanged
        S_VAT: formState.s_VAT || "",
        S_VATRegNo: formState.s_VATRegNo || "",
        S_VARType: formState.s_VARType || "inclusive",
        IsTaxEnabled: Boolean(formState.isTaxEnabled),
        S_TableReservationMarginTime: parseInt(formState.s_TableReservationMarginTime) || 0,
        S_TableReservedRelesaeTime: parseInt(formState.s_TableReservedReleaseTime) || 0,
        S_ShowingReserveredTableTime: parseInt(formState.s_ShowingReserveredTableTime) || 0,
        MaximumReservationDuration: parseInt(formState.maximumReservationDuration) || 0,
        S_IsUpdatefromKitchen: Boolean(formState.s_IsUpdatefromKitchen),
        S_ShowCancelledOrderDuration: parseInt(formState.s_ShowCancelledOrderDuration) || 0,
        S_VisibleCancelOrderDaysinCancelTab: parseInt(formState.s_VisibleCancelOrderDaysinCancelTab) || 0,
        TV_ISOrderStatus: Boolean(formState.tV_ISOrderStatus),
        TV_ISOrderType: Boolean(formState.tV_ISOrderType),
        TV_ISOrderNumber: Boolean(formState.tV_ISOrderNumber),
        TV_ISOrderOwner: Boolean(formState.tV_ISOrderOwner),
        IsActivePreOrderModule: Boolean(formState.isActivePreOrderModule),
        PreOrderingCollection: Boolean(formState.preOrderingCollection),
        PreOrderingDelivery: Boolean(formState.preOrderingDelivery),
        PreOrderDayRange: parseInt(formState.preOrderDayRange) || 0,
        PreOrderAlertInMins: parseInt(formState.preOrderAlertInMins) || 20,
        CompDeliveryStatus: Boolean(formState.compDeliveryStatus),
        CompCollectionStatus: Boolean(formState.compCollectionStatus),
      };

      // Sanitize the payload to ensure proper formatting
      const sanitizedSettings = sanitizeSettingsPayload(updatedSettings);

      const response = await axiosInstance.post(
        APIs.POST_UPDATE_COMPANY_SETTING,
        sanitizedSettings,
        {
          headers: {
            'Content-Type': 'application/json',
          }
        }
      );

      if (response.data.status === true) {
        setFormState((prev) => ({
          ...prev,
          deliveryCharges: 0,
          useFixedDeliveryCharge: false,
        }));
        setUseFixedDeliveryCharge(false);
        showSuccessToast("General settings updated successfully");
        setIsGeneralEditing(false);
        setGeneralPreviousData(null);
      } else {
        const errorMessage = response.data?.Message || response.data?.message || "Failed to update General settings";
        showErrorToast(errorMessage);
      }
    } catch (error) {
      console.error("Error in saveGeneralSettings:", error);
      console.error("Error response:", error.response?.data);
      showErrorToast(
        error.response?.data?.Message ||
        error.response?.data?.message ||
        "Failed to update General settings"
      );
    } finally {
      setLoading(false);
    }
  };

  const saveOnlineOrderingSettings = async () => {
    try {
      setLoading(true);

      // Online Ordering channel toggles + collection payment use UpdateServiceHours.
      const flagsResponse = await axiosInstance.post(
        APIs.POST_UPDATE_SERVICE_HOURS,
        {
          CompID: CompID,
          CompDeliveryStatus: Boolean(formState.compDeliveryStatus),
          CompCollectionStatus: Boolean(formState.compCollectionStatus),
          S_OnlineOrderingCollectionPayment: Boolean(formState.s_OnlineOrderingCollectionPayment),
        },
        { headers: { "Content-Type": "application/json" } }
      );

      if (!flagsResponse.data?.status) {
        showErrorToast(
          flagsResponse.data?.Message ||
            flagsResponse.data?.message ||
            "Failed to update ordering channels"
        );
        return;
      }

      // Email/phone: dedicated upsert (add if missing, update if present).
      const email = (formState.s_OnlineOrderingEmail || "").trim();
      const phone = (formState.s_OnlineOrderingPhone || "").trim();
      const response = await axiosInstance.post(
        APIs.POST_UPDATE_COMPANY_CONTACT_SETTINGS,
        {
          CompID: formState.CompID || CompID,
          S_OnlineOrderingEmail: email || null,
          S_OnlineOrderingPhone: phone || null,
        },
        {
          headers: {
            'Content-Type': 'application/json',
          }
        }
      );

      if (response.data && response.data.status === true) {
        const saved = response.data.data;
        const savedCompId = saved?.compID ?? saved?.CompID ?? 0;
        if (!saved || !savedCompId) {
          showErrorToast(
            response.data?.Message ||
              response.data?.message ||
              "Failed to save online ordering email/phone. Check that SP_UpsertCompanyContactSettings is deployed."
          );
          return;
        }

        setFormState((prev) => ({
          ...prev,
          s_OnlineOrderingEmail: pickSettingsString(
            saved.s_OnlineOrderingEmail,
            saved.S_OnlineOrderingEmail,
            saved.onlineOrderingEmail,
            email
          ),
          s_OnlineOrderingPhone: pickSettingsString(
            saved.s_OnlineOrderingPhone,
            saved.S_OnlineOrderingPhone,
            saved.onlineOrderingPhone,
            phone
          ),
          compDeliveryStatus:
            saved.compDeliveryStatus ?? prev.compDeliveryStatus,
          compCollectionStatus:
            saved.compCollectionStatus ?? prev.compCollectionStatus,
          s_OnlineOrderingCollectionPayment:
            saved.s_OnlineOrderingCollectionPayment ??
            saved.S_OnlineOrderingCollectionPayment ??
            prev.s_OnlineOrderingCollectionPayment,
        }));

        showSuccessToast("Online Ordering settings updated successfully");
        setIsOnlineOrderingEditing(false);
        setOnlineOrderingPreviousData(null);
        await fetchSettings();
      } else {
        const errorMessage = response.data?.Message || response.data?.message || "Failed to update Online Ordering settings";
        showErrorToast(errorMessage);
      }
    } catch (error) {
      console.error("Error in saveOnlineOrderingSettings:", error);
      console.error("Error response:", error.response?.data);
      showErrorToast(
        error.response?.data?.Message ||
        error.response?.data?.message ||
        "Failed to update Online Ordering settings"
      );
    } finally {
      setLoading(false);
    }
  };

  const savePreOrderingSettings = async () => {
    try {
      setLoading(true);

      // Pre-ordering booleans only — does not touch email/phone or Comp* unless included
      const flagsResponse = await axiosInstance.post(
        APIs.POST_UPDATE_SERVICE_HOURS,
        {
          CompID: CompID,
          PreOrderingDelivery: Boolean(formState.preOrderingDelivery),
          PreOrderingCollection: Boolean(formState.preOrderingCollection),
        },
        { headers: { "Content-Type": "application/json" } }
      );

      if (!flagsResponse.data?.status) {
        showErrorToast(
          flagsResponse.data?.Message ||
            flagsResponse.data?.message ||
            "Failed to update pre-ordering channels"
        );
        return;
      }

      // Do not change S_OnlineOrderingEmail / S_OnlineOrderingPhone (Online Ordering tab only).
      const updatedSettings = {
        S_ID: formState.s_ID || 0,
        CompID: formState.CompID,
        IsActivePreOrderModule: Boolean(formState.isActivePreOrderModule),
        PreOrderingCollection: Boolean(formState.preOrderingCollection),
        PreOrderingDelivery: Boolean(formState.preOrderingDelivery),
        PreOrderDayRange: parseInt(formState.preOrderDayRange) || 0,
        PreOrderAlertInMins: parseInt(formState.preOrderAlertInMins) || 20,
        CompDeliveryStatus: Boolean(formState.compDeliveryStatus),
        CompCollectionStatus: Boolean(formState.compCollectionStatus),
        S_OnlineOrderingEmail: null,
        S_OnlineOrderingPhone: null,
        S_ServiceCharges: parseInt(formState.s_ServiceCharges) || 0,
        ...deliveryChargeSettingsPayload(),
        S_MinOrderAmount: parseFloat(formState.s_MinOrderAmount) || 0,
        S_DeliveryTime: parseInt(formState.s_DeliveryTime) || 0,
        S_ReturnPolicy: formState.s_ReturnPolicy || "",
        IsShowWarningOnDeletingCustomerData: Boolean(formState.isShowWarningOnDeletingCustomerData),
        ManageCoversOnTablePlan: Boolean(formState.manageCoversOnTablePlan),
        S_VAT: formState.s_VAT || "",
        S_VATRegNo: formState.s_VATRegNo || "",
        S_VARType: formState.s_VARType || "inclusive",
        IsTaxEnabled: Boolean(formState.isTaxEnabled),
        S_TableReservationMarginTime: parseInt(formState.s_TableReservationMarginTime) || 0,
        S_TableReservedRelesaeTime: parseInt(formState.s_TableReservedReleaseTime) || 0,
        S_ShowingReserveredTableTime: parseInt(formState.s_ShowingReserveredTableTime) || 0,
        MaximumReservationDuration: parseInt(formState.maximumReservationDuration) || 0,
        S_IsUpdatefromKitchen: Boolean(formState.s_IsUpdatefromKitchen),
        S_ShowCancelledOrderDuration: parseInt(formState.s_ShowCancelledOrderDuration) || 0,
        S_VisibleCancelOrderDaysinCancelTab: parseInt(formState.s_VisibleCancelOrderDaysinCancelTab) || 0,
        TV_ISOrderStatus: Boolean(formState.tV_ISOrderStatus),
        TV_ISOrderType: Boolean(formState.tV_ISOrderType),
        TV_ISOrderNumber: Boolean(formState.tV_ISOrderNumber),
        TV_ISOrderOwner: Boolean(formState.tV_ISOrderOwner),
      };

      const response = await axiosInstance.post(
        APIs.POST_UPDATE_COMPANY_SETTING,
        updatedSettings,
        {
          headers: {
            'Content-Type': 'application/json',
          }
        }
      );

      if (response.data && response.data.status === true) {
        showSuccessToast("Pre-Ordering settings updated successfully");
        setIsPreOrderingEditing(false);
        setPreOrderingPreviousData(null);
      } else {
        const errorMessage = response.data?.Message || response.data?.message || "Failed to update Pre-Ordering settings";
        showErrorToast(errorMessage);
      }
    } catch (error) {
      console.error("Error in savePreOrderingSettings:", error);
      showErrorToast(
        error.response?.data?.Message ||
        error.response?.data?.message ||
        "Failed to update Pre-Ordering settings"
      );
    } finally {
      setLoading(false);
    }
  };

  /**
   * Builds AvailabilityTiming day fields from the ScheduleManager state.
   * Hours saves omit the four ordering booleans and never touch email/phone.
   */
  const buildTimingFromSchedules = (name, clearAllDays = false) => {
    const typeMap = {
      weeklyServiceHours: "Restaurant",
      DeliveryTime: "Delivery",
      CollectionTime: "Collection",
    };
    const preferredType = typeMap[name];
    const currentSchedules = schedulesRef.current[name] || schedules[name] || [];
    const currentTimingData = timingDataRef.current[name] || timingData[name] || [];
    const existing = pickPrimaryTiming(currentTimingData, preferredType);
    const existingId = getTimingId(existing);
    // Keep legacy DB type (e.g. "Resturent") so SP_AddUpdateAvailabilityTiming updates
    // the existing row instead of inserting a duplicate "Restaurant" row.
    const existingType = getTimingType(existing);
    const resolvedType =
      existingType && isTimingTypeMatch(existingType, preferredType)
        ? existingType
        : preferredType;

    const timing = {
      AT_ID: existingId,
      CompID: CompID,
      LocalizeID: existing?.localizeID || existing?.LocalizeID || "en",
      Type: resolvedType,
    };
    const days = [
      "Monday",
      "Tuesday",
      "Wednesday",
      "Thursday",
      "Friday",
      "Saturday",
      "Sunday",
    ];

    days.forEach((day) => {
      if (clearAllDays) {
        timing[`AT_${day}Opening`] = null;
        timing[`AT_${day}Closing`] = null;
        return;
      }
      const sObj = currentSchedules.find((s) => s.day === day);
      if (sObj?.checked && sObj.timeSlots?.length > 0) {
        const opening = normalizeTimeToHHmm(sObj.timeSlots[0].from);
        const closing = normalizeTimeToHHmm(sObj.timeSlots[0].to);
        // Only persist a day when both sides parse cleanly.
        if (opening && closing) {
          timing[`AT_${day}Opening`] = opening;
          timing[`AT_${day}Closing`] = closing;
        } else {
          timing[`AT_${day}Opening`] = null;
          timing[`AT_${day}Closing`] = null;
        }
      } else {
        timing[`AT_${day}Opening`] = null;
        timing[`AT_${day}Closing`] = null;
      }
    });

    return timing;
  };

  /**
   * Updates CompDeliveryStatus / CompCollectionStatus (Business Time) or
   * PreOrdering* flags. Does not change hours or online-ordering email/phone.
   */
  const saveOrderingBooleans = async (flags) => {
    try {
      setLoading(true);
      const response = await axiosInstance.post(
        APIs.POST_UPDATE_SERVICE_HOURS,
        {
          CompID: CompID,
          ...flags,
        },
        { headers: { "Content-Type": "application/json" } }
      );

      if (response.data?.status) {
        setFormState((prev) => ({
          ...prev,
          ...(flags.CompDeliveryStatus !== undefined
            ? { compDeliveryStatus: Boolean(flags.CompDeliveryStatus) }
            : {}),
          ...(flags.CompCollectionStatus !== undefined
            ? { compCollectionStatus: Boolean(flags.CompCollectionStatus) }
            : {}),
          ...(flags.PreOrderingDelivery !== undefined
            ? { preOrderingDelivery: Boolean(flags.PreOrderingDelivery) }
            : {}),
          ...(flags.PreOrderingCollection !== undefined
            ? { preOrderingCollection: Boolean(flags.PreOrderingCollection) }
            : {}),
        }));
        showSuccessToast("Ordering settings updated");
      } else {
        showErrorToast(
          response.data?.Message ||
            response.data?.message ||
            "Failed to update ordering settings"
        );
      }
    } catch (error) {
      showErrorToast(
        error.response?.data?.Message ||
          error.response?.data?.message ||
          "Failed to update ordering settings"
      );
    } finally {
      setLoading(false);
    }
  };

  const saveField = async (name, value = editValue) => {

    try {
      setLoading(true);
      const sanitizedValue =
        typeof value === "object" && value !== null
          ? value.target?.checked ?? value
          : value;

      // Handle timing fields via dedicated UpdateServiceHours endpoint
      if (
        ["weeklyServiceHours", "DeliveryTime", "CollectionTime"].includes(name)
      ) {
        // "Set … Hours/Time" off clears day times for that schedule only.
        // Does not change CompDeliveryStatus / CompCollectionStatus (toggled separately).
        const clearAllDays = !enableScheduleRef.current[name];
        const timing = buildTimingFromSchedules(name, clearAllDays);

        const updatedTimingData = { ...timingDataRef.current };
        updatedTimingData[name] = [timing];
        setTimingData(updatedTimingData);

        const payload = {
          CompID: CompID,
          EnableRestaurantSchedule:
            name === "weeklyServiceHours"
              ? enableScheduleRef.current.weeklyServiceHours
              : true,
          EnableDeliverySchedule:
            name === "DeliveryTime"
              ? enableScheduleRef.current.DeliveryTime
              : true,
          EnableCollectionSchedule:
            name === "CollectionTime"
              ? enableScheduleRef.current.CollectionTime
              : true,
          RestaurantTiming:
            name === "weeklyServiceHours" ? timing : null,
          DeliveryTiming: name === "DeliveryTime" ? timing : null,
          CollectionTiming: name === "CollectionTime" ? timing : null,
        };

        const response = await axiosInstance.post(
          APIs.POST_UPDATE_SERVICE_HOURS,
          payload,
          {
            headers: {
              "Content-Type": "application/json",
            },
          }
        );

        if (response.data?.status) {
          showSuccessToast(`${name} updated successfully`);
          // Always re-fetch so UI matches the row that was actually updated
          // (avoids stale duplicate Restaurant/Resturent rows in the POST response).
          await fetchSettings();
        } else {
          showErrorToast(
            response.data?.Message ||
              response.data?.message ||
              "Failed to update"
          );
        }
        cancelEditingAfterSubmit();
        return;
      }

      // Handle tax section save
      if (name === "Tax") {

        const updatedSettings = {
          S_ID: formState.s_ID || 0,
          CompID: formState.CompID,
          // Update only VAT fields
          IsTaxEnabled: Boolean(formState.isTaxEnabled),
          S_VAT: parseInt(formState.s_VAT) || 0,
          S_VATRegNo: formState.s_VATRegNo || "",
          S_VARType: formState.s_VARType || "Inclusive",
          // Keep all other fields unchanged
          S_ServiceCharges: parseInt(formState.s_ServiceCharges) || 0,
          ...deliveryChargeSettingsPayload(),
          S_MinOrderAmount: parseFloat(formState.s_MinOrderAmount) || 0,
          S_DeliveryTime: parseInt(formState.s_DeliveryTime) || 0,
          S_ReturnPolicy: formState.s_ReturnPolicy || "",
          IsShowWarningOnDeletingCustomerData: Boolean(formState.isShowWarningOnDeletingCustomerData),
          ManageCoversOnTablePlan: Boolean(formState.manageCoversOnTablePlan),
          S_TableReservationMarginTime: parseInt(formState.s_TableReservationMarginTime) || 0,
          S_TableReservedRelesaeTime: parseInt(formState.s_TableReservedReleaseTime) || 0,
          S_ShowingReserveredTableTime: parseInt(formState.s_ShowingReserveredTableTime) || 0,
          MaximumReservationDuration: parseInt(formState.maximumReservationDuration) || 0,
          S_IsUpdatefromKitchen: Boolean(formState.s_IsUpdatefromKitchen),
          S_ShowCancelledOrderDuration: parseInt(formState.s_ShowCancelledOrderDuration) || 0,
          S_VisibleCancelOrderDaysinCancelTab: parseInt(formState.s_VisibleCancelOrderDaysinCancelTab) || 0,
        TV_ISOrderStatus: Boolean(formState.tV_ISOrderStatus),
          TV_ISOrderType: Boolean(formState.tV_ISOrderType),
          TV_ISOrderNumber: Boolean(formState.tV_ISOrderNumber),
          TV_ISOrderOwner: Boolean(formState.tV_ISOrderOwner),
          IsActivePreOrderModule: Boolean(formState.isActivePreOrderModule),
          PreOrderingCollection: Boolean(formState.preOrderingCollection),
          PreOrderingDelivery: Boolean(formState.preOrderingDelivery),
          PreOrderDayRange: parseInt(formState.preOrderDayRange) || 0,
          PreOrderAlertInMins: parseInt(formState.preOrderAlertInMins) || 20,
          CompDeliveryStatus: Boolean(formState.compDeliveryStatus),
          CompCollectionStatus: Boolean(formState.compCollectionStatus),
        };
        console.log("Updating VAT settings with data:", updatedSettings);
 

        const response = await axiosInstance.post(
          APIs.POST_UPDATE_COMPANY_SETTING,
          updatedSettings,
          {
            headers: {
              'Content-Type': 'application/json',
            }
          }
        );


        if (response.data.status) {
          showSuccessToast("VAT settings updated successfully");
          setTaxEditing(false);
          setPreviousSettingsData(null);
        } else {
          showErrorToast(response.data.Message || "Failed to update VAT settings");
        }

        return;
      }

      setFormState((prev) => ({
        ...prev,
        [name]: sanitizedValue,
      }));

      // Create complete settings data with only the specific field updated
      const completeSettingsData = {
        S_ID: formState.s_ID || 0,
        CompID: formState.CompID,
        // Update only the specific field
        [name]: sanitizedValue,
        // Keep all other fields unchanged
        S_ServiceCharges: parseInt(formState.s_ServiceCharges) || 0,
        ...deliveryChargeSettingsPayload(),
        S_MinOrderAmount: parseFloat(formState.s_MinOrderAmount) || 0,
        S_DeliveryTime: parseInt(formState.s_DeliveryTime) || 0,
        S_ReturnPolicy: formState.s_ReturnPolicy || "",
        IsShowWarningOnDeletingCustomerData: Boolean(formState.isShowWarningOnDeletingCustomerData),
        ManageCoversOnTablePlan: Boolean(formState.manageCoversOnTablePlan),
        S_VAT: formState.s_VAT || "",
        S_VATRegNo: formState.s_VATRegNo || "",
        S_VARType: formState.s_VARType || "inclusive",
        IsTaxEnabled: Boolean(formState.isTaxEnabled),
        S_TableReservationMarginTime: parseInt(formState.s_TableReservationMarginTime) || 0,
        S_TableReservedRelesaeTime: parseInt(formState.s_TableReservedReleaseTime) || 0,
        S_ShowingReserveredTableTime: parseInt(formState.s_ShowingReserveredTableTime) || 0,
        MaximumReservationDuration: parseInt(formState.maximumReservationDuration) || 0,
        S_IsUpdatefromKitchen: Boolean(formState.s_IsUpdatefromKitchen),
        S_ShowCancelledOrderDuration: parseInt(formState.s_ShowCancelledOrderDuration) || 0,
        S_VisibleCancelOrderDaysinCancelTab: parseInt(formState.s_VisibleCancelOrderDaysinCancelTab) || 0,
        TV_ISOrderStatus: Boolean(formState.tV_ISOrderStatus),
        TV_ISOrderType: Boolean(formState.tV_ISOrderType),
        TV_ISOrderNumber: Boolean(formState.tV_ISOrderNumber),
        TV_ISOrderOwner: Boolean(formState.tV_ISOrderOwner),
        IsActivePreOrderModule: Boolean(formState.isActivePreOrderModule),
        PreOrderingCollection: Boolean(formState.preOrderingCollection),
        PreOrderingDelivery: Boolean(formState.preOrderingDelivery),
        PreOrderDayRange: parseInt(formState.preOrderDayRange) || 0,
        PreOrderAlertInMins: parseInt(formState.preOrderAlertInMins) || 20,
        CompDeliveryStatus: Boolean(formState.compDeliveryStatus),
        CompCollectionStatus: Boolean(formState.compCollectionStatus),
      };

      const requestPayload = {
        updatedSettings: completeSettingsData
      };

      const response = await axiosInstance.post(
        APIs.POST_UPDATE_COMPANY_SETTING,
        requestPayload,
        {
          headers: {
            'Content-Type': 'application/json',
          }
        }
      );

      if (response.data.status) {
        showSuccessToast("Setting Data Updated successfully");
      } else {
        showErrorToast(response.data.Message || "Failed to update");
      }

      cancelEditingAfterSubmit();
    } catch (error) {
      showErrorToast(`Failed to update ${name}`);
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentMethodSubmit = async (paymentData, isUpdate) => {
    try {
      setLoading(true);
      const url = isUpdate
        ? `${APIs.UPDATE_PAYMENT_METHOD}`
        : `${APIs.ADD_PAYMENT_METHOD}`;
      const method = isUpdate ? "patch" : "post";

      const response = await axiosInstance[method](url, paymentData);

      if (response.data.status) {
        showSuccessToast(
          response.data.Message ||
          `Payment method ${isUpdate ? "updated" : "added"} successfully`
        );

        if (isUpdate) {
          setPaymentMethods((prev) =>
            prev.map((pm) =>
              pm.id === paymentData.ID ? { ...pm, ...paymentData } : pm
            )
          );
        } else {
          setPaymentMethods((prev) => [...prev, paymentData]);
        }
      } else {
        showErrorToast(
          response.data.Message ||
          `Failed to ${isUpdate ? "update" : "add"} payment method`
        );
      }
    } catch (error) {
      showErrorToast(
        error.response?.data?.Message ||
        `Error ${isUpdate ? "updating" : "adding"} payment method`
      );
    } finally {
      setLoading(false);
    }
  };

  const handleSubscriptionSubmit = async (paymentMethod) => {
    try {
      setLoading(true);
      const response = await axiosInstance.post(APIs.SUBSCRIBE_PLUS_PLAN, {
        compID: CompID,
        subscriptionDate: new Date().toISOString(),
        paymentMethod: paymentMethod,
      });

      if (response.data.status) {
        showSuccessToast(
          response.data.Message || "Successfully subscribed to Plus plan"
        );
      } else {
        showErrorToast(
          response.data.Message || "Failed to subscribe to Plus plan"
        );
      }
    } catch (error) {
      showErrorToast(
        error.response?.data?.Message || "Error subscribing to Plus plan"
      );
    } finally {
      setLoading(false);
    }
  };

  const renderOnlineOrderingField = (label, name, value, type = "text", options = null) => {
    return (
      <div className="list-group-item d-flex align-items-center">
        <div className="flex-fill">
          <div>{label}</div>
          <div className="mt-2">
            {type === "checkbox" ? (
              <ToggleSwitch
                isChecked={value}
                onToggle={(checked) => {
                  setFormState((prev) => ({
                    ...prev,
                    [name]: checked,
                  }));
                }}
                label={label}
                name={name}
                themeColor={themeColor}
              />
            ) : type === "select" ? (
              <Select
                options={options}
                value={options.find((o) => o.value === value)}
                onChange={(option) => {
                  setFormState((prev) => ({
                    ...prev,
                    [name]: option ? option.value : "",
                  }));
                }}
                placeholder={`Select ${label}`}
                isClearable
              />
            ) : type === "textarea" ? (
              <textarea
                className="form-control"
                rows="4"
                value={value}
                onChange={(e) => {
                  setFormState((prev) => ({
                    ...prev,
                    [name]: e.target.value,
                  }));
                }}
                placeholder={`Enter ${label}`}
                name={name}
              />
            ) : (
              <FormInput
                type={type}
                value={value}
                onchange={(e) => {
                  const sanitizedValue = [
                    "s_ServiceCharges",
                    "s_VAT",
                    "s_ServicesChargesOnCovers",
                    "s_MinOrderAmount",
                    "s_DeliveryTime",
                    "s_TableReservationMarginTime",
                    "s_TableReservedReleaseTime",
                    "s_ShowingReserveredTableTime",
                    "s_ShowCancelledOrderDuration",
                    "s_VisibleCancelOrderDaysinCancelTab",
                    "preOrderAlertInMins",
                  ].includes(name)
                    ? isNaN(Number(e.target.value)) || e.target.value === ""
                      ? ""
                      : Number(e.target.value)
                    : e.target.value;

                  if (typeof sanitizedValue === "number" && sanitizedValue < 0) {
                    showErrorToast(
                      `${name.replace(/([A-Z])/g, " $1").trim()} must be a positive number`
                    );
                    return;
                  }

                  setFormState((prev) => ({
                    ...prev,
                    [name]: sanitizedValue,
                  }));
                }}
                placeHolder={`Enter ${label}`}
                name={name}
              />
            )}
          </div>
          <div className="text-muted mt-1">
            {type === "checkbox"
              ? value
                ? "Enabled"
                : "Disabled"
              : value || "Not set"}
          </div>
        </div>
      </div>
    );
  };

  const renderServiceHoursDisplay = (name, value, label) => {
    const days = [
      "Monday",
      "Tuesday",
      "Wednesday",
      "Thursday",
      "Friday",
      "Saturday",
      "Sunday",
    ];

    const primary = pickPrimaryTiming(value) || value?.[0];
    const hasSchedule =
      !!primary &&
      days.some((day) =>
        normalizeTimeToHHmm(getTimingDayValue(primary, day, "Opening"))
      );

    if (!hasSchedule) {
      return (
        <div className="text-muted">
          <span className="badge bg-info">
            <i className="fas fa-clock me-1"></i>
            Not configured
          </span>
        </div>
      );
    }

    const activeDays = days.filter((day) => {
      const opening = normalizeTimeToHHmm(
        getTimingDayValue(primary, day, "Opening")
      );
      const closing = normalizeTimeToHHmm(
        getTimingDayValue(primary, day, "Closing")
      );
      return opening && closing;
    });

    if (activeDays.length === 0) {
      return (
        <div className="text-muted">
          <span className="badge bg-info">
            <i className="fas fa-exclamation-triangle me-1"></i>
            No active days
          </span>
        </div>
      );
    }

    return (
      <div className="schedule-display">
        <div className="row g-2">
          {days.map((day) => {
            const openingTime = normalizeTimeToHHmm(
              getTimingDayValue(primary, day, "Opening")
            );
            const closingTime = normalizeTimeToHHmm(
              getTimingDayValue(primary, day, "Closing")
            );
            const isActive = !!(openingTime && closingTime);

            return (
              <div key={day} className="col-md-6 col-lg-4">
                <div className={`d-flex align-items-center p-2 rounded ${isActive ? 'bg-light-success' : 'bg-light-secondary'
                  }`}>
                  <div className="flex-fill">
                    <div className="fw-semibold text-sm">{day}</div>
                    <div className={`text-xs ${isActive ? 'text-success' : 'text-muted'
                      }`}>
                      {isActive ? (
                        <>
                          <i className="fas fa-clock me-1"></i>
                          {formatTimeForDisplay(openingTime)} - {formatTimeForDisplay(closingTime)}
                        </>
                      ) : (
                        <>
                          <i className="fas fa-times me-1"></i>
                          Closed
                        </>
                      )}
                    </div>
                  </div>
                  <div className="ms-2">
                    {isActive ? (
                      <span className="badge bg-info">
                        <i className="fas fa-check"></i>
                      </span>
                    ) : (
                      <span className="badge bg-info">
                        <i className="fas fa-times"></i>
                      </span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-3">
          <div className="d-flex align-items-center text-muted">
            <i className="fas fa-info-circle me-2"></i>
            <span className="text-sm">
              {activeDays.length} of {days.length} days active
            </span>
          </div>
        </div>
      </div>
    );
  };

  const renderField = (label, name, value, type = "text", options = null) => {
    const isEditing = editingField === name;
    const days = [
      "Monday",
      "Tuesday",
      "Wednesday",
      "Thursday",
      "Friday",
      "Saturday",
      "Sunday",
    ];

    return (
      <div className="list-group-item d-flex align-items-center">
        <div className="flex-fill">
          <div>{label}</div>
          {isEditing ? (
            <div className="mt-2">
              {[
                "weeklyServiceHours",
                "DeliveryTime",
                "CollectionTime",
              ].includes(name) ? (
                <div
                  className="card border-0 p-3"
                  style={{ backgroundColor: "#f5f5f5" }}
                >
                  <h5>{label}</h5>
                  <div className="mb-3">
                    <ToggleSwitch
                      isChecked={enableSchedule[name]}
                      onToggle={() =>
                        setEnableSchedule((prev) => ({
                          ...prev,
                          [name]: !prev[name],
                        }))
                      }
                      label={`Set ${label.toLowerCase()}`}
                      name={`enableSchedule-${name}`}
                      themeColor={themeColor}
                    />
                    <p className="text-muted mt-2">
                      Set the days of the week and times of day for{" "}
                      {label.toLowerCase()}.
                    </p>
                  </div>
                  {enableSchedule[name] && (
                    <div className="mb-4">
                      <div className="row">
                        <div className="col-12">
                          <ScheduleManager
                            schedules={schedules[name]}
                            setSchedules={(updatedSchedules) =>
                              setSchedules((prev) => ({
                                ...prev,
                                [name]:
                                  typeof updatedSchedules === "function"
                                    ? updatedSchedules(prev[name])
                                    : updatedSchedules,
                              }))
                            }
                            isProfilePage={true}
                          />
                        </div>
                      </div>
                    </div>
                  )}
                  <div className="mt-3">
                    <button
                      className="btn btn-rounded px-4 rounded-pill me-2"
                      style={{
                        backgroundColor: themeColor,
                        color: 'white',
                        border: 'none',
                        transition: 'all 0.3s ease'
                      }}
                      onClick={() => saveField(name)}
                      disabled={loading}
                    >
                      Save
                    </button>
                    <button
                      className="btn settings-btn settings-btn--ghost"
                      onClick={cancelEditing}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : type === "checkbox" ? (
                <ToggleSwitch
                  isChecked={value}
                  onToggle={(checked) => handleToggleChange(name, checked)}
                  label={label}
                  name={name}
                  themeColor={themeColor}
                />
              ) : type === "select" ? (
                <Select
                  options={options}
                  value={options.find((o) => o.value === editValue)}
                  onChange={(option) => handleSelectChange(name, option)}
                  placeholder={`Select ${label}`}
                  isClearable
                />
              ) : type === "textarea" ? (
                <textarea
                  className="form-control"
                  rows="4"
                  value={editValue}
                  onChange={(e) => handleInputChange(e)}
                  placeholder={`Enter ${label}`}
                  name={name}
                />
              ) : (
                <FormInput
                  type={type}
                  value={editValue}
                  onchange={(e) => handleInputChange(e)}
                  placeHolder={`Enter ${label}`}
                  name={name}
                />
              )}
              {type !== "checkbox" &&
                ![
                  "weeklyServiceHours",
                  "DeliveryTime",
                  "CollectionTime",
                ].includes(name) && (
                  <div className="mt-2">
                    <button
                      className="btn btn-success btn-sm me-2"
                      onClick={() => saveField(name)}
                      disabled={loading}
                    >
                      Save
                    </button>
                    <button
                      className="btn settings-btn settings-btn--ghost"
                      onClick={cancelEditing}
                      disabled={loading}
                    >
                      Cancel
                    </button>
                  </div>
                )}
            </div>
          ) : (
            <div className="text-muted">
              {[
                "weeklyServiceHours",
                "DeliveryTime",
                "CollectionTime",
              ].includes(name)
                ? value.length > 0 &&
                  value[0] &&
                  (value[0].aT_MondayOpening ||
                    value[0].aT_TuesdayOpening ||
                    value[0].aT_WednesdayOpening ||
                    value[0].aT_ThursdayOpening ||
                    value[0].aT_FridayOpening ||
                    value[0].aT_SaturdayOpening ||
                    value[0].aT_SundayOpening)
                  ? days
                    .filter(
                      (day) =>
                        value[0][`aT_${day}Opening`] &&
                        value[0][`aT_${day}Closing`]
                    )
                    .map(
                      (day) =>
                        `${day}: ${formatTimeForDisplay(
                          value[0][`aT_${day}Opening`]
                        )} - ${formatTimeForDisplay(
                          value[0][`aT_${day}Closing`]
                        )}`
                    )
                    .join(", ")
                  : "Not set"
                : type === "checkbox"
                  ? value
                    ? "Enabled"
                    : "Disabled"
                  : value || "Not set"}
            </div>
          )}
        </div>
        {!isEditing && (
          <div>
            <button
              className="btn settings-btn settings-btn--ghost"
              onClick={() => startEditing(name, value)}
            >
              Edit
            </button>
          </div>
        )}
      </div>
    );
  };

  const renderTaxSection = () => {
    return (
      <div id="Tax" className="settings-tab-panel">
        {renderSectionHeading(
          "VAT Management",
          "Configure VAT settings for order-level and item-level tax."
        )}

        {/* VAT Configuration Card - View Mode */}
        {!taxEditing && (
          <div className="card settings-section border-0 mb-3">
            <div className="card-body">
              <div className="d-flex align-items-center mb-3">
                <div className="flex-fill">
                  <div className="d-flex align-items-center mb-2">
                    <i className="fas fa-percentage me-2" style={{ color: themeColor }}></i>
                    <h5 className="mb-0 fw-semibold" style={{ color: "var(--settings-text)", fontSize: "14px" }}>% VAT On Order Level</h5>
                  </div>
                  {renderTaxStatusDisplay()}
                </div>
              </div>
              <div className="d-flex justify-content-end">
                <button
                  type="button"
                  className="btn settings-btn settings-btn--ghost"
                  onClick={() => startEditing("Tax", null)}
                >
                  Edit
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Edit Mode */}
        {taxEditing && (
          <div
            className="card settings-section border-0"
            style={{ borderColor: themeColor, borderWidth: "2px", borderStyle: "solid" }}
          >
            <div className="card-body">
              <div className="d-flex align-items-center mb-3">
                <i className="fas fa-percentage me-2" style={{ color: themeColor }}></i>
                <h5 className="mb-0 fw-semibold">% VAT On Order Level</h5>
              </div>

              <div className="mb-4">
                <ToggleSwitch
                  isChecked={formState.isTaxEnabled}
                  onToggle={(checked) => {
                    // When disabling tax, reset all tax-related fields to default values
                    if (!checked) {
                      setFormState((prev) => ({
                        ...prev,
                        isTaxEnabled: false,
                        s_VAT: "",
                        s_VATRegNo: "",
                        s_VARType: "inclusive",
                      }));
                    } else {
                      setFormState((prev) => ({
                        ...prev,
                        isTaxEnabled: true,
                      }));
                    }
                  }}
                  label="Enable VAT On Order Level"
                  name="isTaxEnabled"
                  themeColor={themeColor}
                />
                <small className="text-muted mt-2 d-block">
                  (If enabled, VAT will be applied to Whole Order Amount){" "}
                  <b>AND</b> (if disabled VAT will be applied to each item individually)
                </small>
              </div>

              {/* Tax Detail Fields - only show when tax is enabled */}
              {formState.isTaxEnabled && (
                <div className="row g-3 mb-4">
                  {/* VAT Percentage */}
                  <div className="col-md-4">
                    <div className="card h-100">
                      <div className="card-body">
                        <div className="d-flex align-items-center mb-2">
                          <i className="fas fa-percentage me-2" style={{ color: themeColor }}></i>
                          <h6 className="mb-0 fw-semibold">VAT Rate</h6>
                        </div>
                        <FormInput
                          type="number"
                          value={formState.s_VAT}
                          onchange={(e) => {
                            const sanitizedValue = isNaN(Number(e.target.value)) || e.target.value === ""
                              ? ""
                              : Number(e.target.value);

                            if (typeof sanitizedValue === "number" && sanitizedValue < 0) {
                              showErrorToast("VAT must be a positive number");
                              return;
                            }

                            setFormState((prev) => ({
                              ...prev,
                              s_VAT: sanitizedValue,
                            }));
                          }}
                          placeHolder="Enter VAT percentage"
                          name="s_VAT"
                          min="0"
                          step="0.01"
                        />
                        <small className="text-muted">Percentage rate for VAT calculation</small>
                      </div>
                    </div>
                  </div>

                  {/* VAT VAT Registration Number */}
                  <div className="col-md-4">
                    <div className="card h-100">
                      <div className="card-body">
                        <div className="d-flex align-items-center mb-2">
                          <i className="fas fa-id-card me-2" style={{ color: themeColor }}></i>
                          <h6 className="mb-0 fw-semibold">VAT Registration</h6>
                        </div>
                        <FormInput
                          type="text"
                          value={formState.s_VATRegNo}
                          onchange={(e) => handleInputChange(e)}
                          placeHolder="Enter VAT registration number"
                          name="s_VATRegNo"
                        />
                        <small className="text-muted">Official VAT Registration Number</small>
                      </div>
                    </div>
                  </div>

                  {/* VAT Type */}
                  <div className="col-md-4">
                    <div className="card h-100">
                      <div className="card-body">
                        <div className="d-flex align-items-center mb-2">
                          <i className="fas fa-tags me-2" style={{ color: themeColor }}></i>
                          <h6 className="mb-0 fw-semibold">VAT Type</h6>
                        </div>
                        <div className="form-check mb-2">
                          <input
                            className="form-check-input"
                            type="radio"
                            name="s_VARType"
                            id="s_VARType-inclusive"
                            value="inclusive"
                            checked={formState.s_VARType === "inclusive"}
                            onChange={(e) => handleInputChange(e)}
                          />
                          <label className="form-check-label" htmlFor="s_VARType-inclusive">
                            Inclusive
                          </label>
                        </div>
                        <div className="form-check">
                          <input
                            className="form-check-input"
                            type="radio"
                            name="s_VARType"
                            id="s_VARType-exclusive"
                            value="exclusive"
                            checked={formState.s_VARType === "exclusive"}
                            onChange={(e) => handleInputChange(e)}
                          />
                          <label className="form-check-label" htmlFor="s_VARType-exclusive">
                            Exclusive
                          </label>
                        </div>
                        <small className="text-muted">How VAT is calculated and displayed</small>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div className="d-flex justify-content-end gap-2 mt-3">
                <button
                  className="btn settings-btn settings-btn--ghost"
                  onClick={cancelEditing}
                  disabled={loading}
                >
                  <i className="fas fa-times me-1"></i>
                  Cancel
                </button>
                <button
                  className="btn settings-btn settings-btn--primary"
                  style={{
                    backgroundColor: themeColor,
                    color: 'white',
                    border: 'none',
                    transition: 'all 0.3s ease'
                  }}
                  onClick={() => {
                    console.log("VAT Save button clicked");
                    saveField("Tax");
                  }}
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                      Saving...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-save me-1"></i>
                      Save Changes
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderTaxStatusDisplay = () => {
    if (!formState.isTaxEnabled) {
      return (
        <div className="text-muted">
          <span className="badge bg-info fs-6">
            <i className="fas fa-times me-1"></i>
            Disabled
          </span>
          <div className="mt-2">
            <small className="text-muted">
              <i className="fas fa-info-circle me-1"></i>
              VAT is currently disabled for this business
            </small>
          </div>
        </div>
      );
    }

    return (
      <div className="tax-status-display">
        <div className="d-flex align-items-center mb-2">
          <span className="badge bg-info fs-6 me-3">
            <i className="fas fa-check me-1"></i>
            Enabled
          </span>
        </div>

        <div className="row g-2">
          <div className="col-md-4">
            <div className="d-flex align-items-center p-2 bg-light-success rounded">
              <i className="fas fa-percentage me-2"></i>
              <div>
                <div className="fw-semibold text-sm">VAT Rate</div>
                <div className="text-success text-xs">
                  {formState.s_VAT || 0}%
                </div>
              </div>
            </div>
          </div>

          <div className="col-md-4">
            <div className="d-flex align-items-center p-2 bg-light-warning rounded">
              <i className="fas fa-id-card me-2"></i>
              <div>
                <div className="fw-semibold text-sm">VAT Registration</div>
                <div className="text-success text-xs">
                  {formState.s_VATRegNo || "Not set"}
                </div>
              </div>
            </div>
          </div>

          <div className="col-md-4">
            <div className="d-flex align-items-center p-2 bg-light-primary rounded">
              <i className="fas fa-tags me-2"></i>
              <div>
                <div className="fw-semibold text-sm">Type</div>
                <div className="text-primary text-xs">
                  {formState.s_VARType === "inclusive" ? "Inclusive" : "Exclusive"}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-3">
          <div className="d-flex align-items-center text-muted">
            <i className="fas fa-info-circle me-2"></i>
            <span className="text-sm">
              VAT is applied {formState.s_VARType === "inclusive" ? "inclusively" : "exclusively"} at {formState.s_VAT || 0}%
            </span>
          </div>
        </div>
      </div>
    );
  }


  return (
    <>
      <div
        className={`container-fluid px-3 px-md-4 py-3 settings-page${isDarkMode ? " settings-page--dark" : ""}`}
        style={settingsThemeStyle}
      >
        <div className="d-flex align-items-center mb-3 settings-page-header">
          <div>
            <ol className="breadcrumb mb-1">
              <li className="breadcrumb-item">
                <a href="#/">Home</a>
              </li>
              <li className="breadcrumb-item active">Settings</li>
            </ol>
            <h1 className="page-header mb-0">
              <i className="fa fa-cog me-2" aria-hidden="true"></i>
              Settings
            </h1>
            <p className="settings-page-subtitle">
              Configure charges, tax, hours, ordering, and printing for your business.
            </p>
          </div>
        </div>

        <div className="settings-tabs-wrap">
          <ul className="settings-tabs" role="tablist">
            {SETTINGS_TABS.filter((tab) => !tab.hidden).map((tab) => (
              <li key={tab.id} className="nav-item" role="presentation">
                <button
                  type="button"
                  role="tab"
                  aria-selected={activeSettingsTab === tab.id}
                  className={`settings-tabs__btn${activeSettingsTab === tab.id ? " is-active" : ""}`}
                  onClick={() => setActiveSettingsTab(tab.id)}
                >
                  <i className={`${tab.icon} me-2`} aria-hidden="true" />
                  {tab.label}
                </button>
              </li>
            ))}
          </ul>
        </div>

        <GridSectionLoader loading={loading} minHeight="400px">
        <div className="row" style={{ marginLeft: 0, marginRight: 0 }}>
          <div className="col-12" id="bsSpyContent" style={{ flex: "1", maxWidth: "none" }}>
            {activeSettingsTab === "charges" && (
            <div id="charges" className="settings-tab-panel">
              {renderSectionHeading(
                "Charges",
                "Service charges, delivery fees, minimum order amounts, and return policy."
              )}
              <div className="card settings-section border-0">
                <div className="list-group list-group-flush">
                  {!isGeneralEditing ? (
                    <>
                      {/* Service Charges */}
                      <div className="list-group-item">
                        <div className="row g-3">
                          <div className="col-md-12">
                            <div className="d-flex align-items-center mb-1">
                              <i className="fas fa-percentage me-2" style={{ color: themeColor }}></i>
                              <span className="fw-semibold">Service Charges (%)</span>
                            </div>
                            <div className="text-muted">
                              <span className="badge bg-info">
                                {formState.s_ServiceCharges ? `${formState.s_ServiceCharges}%` : "Not set"}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Delivery Charges Section - View Mode (postcode rules only) */}
                      <DeliveryChargeRules
                        CompID={CompID}
                        currencySymbol={currencySymbol}
                      />

                      {/* Second Row - Minimum Order Amount and Delivery Time */}
                      <div className="list-group-item">
                        <div className="row g-3">
                          <div className="col-md-6">
                            <div className="d-flex align-items-center mb-1">
                              <i className="fas fa-money-bill-wave me-2" style={{ color: themeColor }}></i>
                              <span className="fw-semibold">Minimum Order Amount</span>
                            </div>
                            <div className="text-muted">
                              <span className="badge bg-info">
                                {formState.s_MinOrderAmount ? `${currencySymbol}${formState.s_MinOrderAmount}` : "Not set"}
                              </span>
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="d-flex align-items-center mb-1">
                              <i className="fas fa-clock me-2" style={{ color: themeColor }}></i>
                              <span className="fw-semibold">Delivery Time (min)</span>
                            </div>
                            <div className="text-muted">
                              <span className="badge bg-info">
                                {formState.s_DeliveryTime ? `${formState.s_DeliveryTime} minutes` : "Not set"}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Return Policy - View Mode */}
                      <div className="list-group-item d-flex align-items-center">
                        <div className="flex-fill">
                          <div className="d-flex align-items-center mb-1">
                            <i className="fas fa-undo me-2" style={{ color: themeColor }}></i>
                            <span className="fw-semibold">Return Policy</span>
                          </div>
                          <div className="text-muted">
                            <span className="badge bg-info">
                              {formState.s_ReturnPolicy ? "Set" : "Not set"}
                            </span>
                            {formState.s_ReturnPolicy && (
                              <div className="mt-2">
                                <small className="text-muted">
                                  {formState.s_ReturnPolicy.length > 100
                                    ? `${formState.s_ReturnPolicy.substring(0, 100)}...`
                                    : formState.s_ReturnPolicy}
                                </small>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

            
                      {/* <div className="list-group-item bg-light">
                        <div className="d-flex align-items-center mb-3">
                          <i className="fas fa-exclamation-triangle me-2" style={{ color: themeColor }}></i>
                          <span className="fw-bold text-dark">Warning Settings</span>
                        </div>
                        <div className="row">
                          <div className="col-md-6">
                            <div className="d-flex align-items-center">
                              <div className="flex-fill">
                                <div className="d-flex align-items-center mb-1">
                                  <i className="fas fa-user-times me-2" style={{ color: themeColor }}></i>
                                  <span className="fw-semibold">Show Warning On Deleting Customer Data</span>
                                </div>
                                <div className="text-muted">
                                  <span className="badge bg-info">
                                    {formState.isShowWarningOnDeletingCustomerData ? "Enabled" : "Disabled"}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="d-flex align-items-center">
                              <div className="flex-fill">
                                <div className="d-flex align-items-center mb-1">
                                  <i className="fas fa-chair me-2" style={{ color: themeColor }}></i>
                                  <span className="fw-semibold">Manage Covers of Table Plan</span>
                                </div>
                                <div className="text-muted">
                                  <span className="badge bg-info">
                                    {formState.manageCoversOnTablePlan ? "Enabled" : "Disabled"}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div> */}

                      {/* Single Edit Button */}
                      <div className="list-group-item d-flex justify-content-end">
                        <button
                          className="btn settings-btn settings-btn--ghost"
                          onClick={startGeneralEditing}
                        >
                          Edit
                        </button>
                      </div>
                    </>
                  ) : (
                    <>
                      {/* Service Charges */}
                      <div className="list-group-item">
                        <div className="row g-3">
                          <div className="col-md-12">
                            <div className="d-flex align-items-center mb-2">
                              <i className="fas fa-percentage me-2" style={{ color: themeColor }}></i>
                              <span className="fw-semibold">Service Charges (%)</span>
                            </div>
                            <FormInput
                              type="number"
                              value={formState.s_ServiceCharges}
                              onchange={(e) => {
                                const sanitizedValue = isNaN(Number(e.target.value)) || e.target.value === ""
                                  ? ""
                                  : Number(e.target.value);

                                if (typeof sanitizedValue === "number" && sanitizedValue < 0) {
                                  showErrorToast("Service Charges must be a positive number");
                                  return;
                                }

                                setFormState((prev) => ({
                                  ...prev,
                                  s_ServiceCharges: sanitizedValue,
                                }));
                              }}
                              placeHolder="Enter service charges percentage"
                              name="s_ServiceCharges"
                              min="0"
                              step="0.01"
                            />
                          </div>
                        </div>
                      </div>

                      {/* Delivery Charges Section - Edit Mode (postcode rules only) */}
                      <DeliveryChargeRules
                        CompID={CompID}
                        currencySymbol={currencySymbol}
                      />

                      {/* Second Row - Minimum Order Amount and Delivery Time */}
                      <div className="list-group-item">
                        <div className="row g-3">
                          <div className="col-md-6">
                            <div className="d-flex align-items-center mb-2">
                              <i className="fas fa-money-bill-wave me-2" style={{ color: themeColor }}></i>
                              <span className="fw-semibold">Minimum Order Amount</span>
                            </div>
                            <FormInput
                              type="number"
                              value={formState.s_MinOrderAmount}
                              onchange={(e) => {
                                const sanitizedValue = isNaN(Number(e.target.value)) || e.target.value === ""
                                  ? ""
                                  : Number(e.target.value);

                                if (typeof sanitizedValue === "number" && sanitizedValue < 0) {
                                  showErrorToast("Minimum Order Amount must be a positive number");
                                  return;
                                }

                                setFormState((prev) => ({
                                  ...prev,
                                  s_MinOrderAmount: sanitizedValue,
                                }));
                              }}
                              placeHolder={`${currencySymbol}0.00`}
                              name="s_MinOrderAmount"
                              min="0"
                              step="0.01"
                              prepend={currencySymbol}
                            />
                          </div>
                          <div className="col-md-6">
                            <div className="d-flex align-items-center mb-2">
                              <i className="fas fa-clock text-primary me-2"></i>
                              <span className="fw-semibold">Delivery Time (min)</span>
                            </div>
                            <FormInput
                              type="number"
                              value={formState.s_DeliveryTime}
                              onchange={(e) => {
                                const sanitizedValue = isNaN(Number(e.target.value)) || e.target.value === ""
                                  ? ""
                                  : Number(e.target.value);

                                if (typeof sanitizedValue === "number" && sanitizedValue < 0) {
                                  showErrorToast("Delivery Time must be a positive number");
                                  return;
                                }

                                setFormState((prev) => ({
                                  ...prev,
                                  s_DeliveryTime: sanitizedValue,
                                }));
                              }}
                              placeHolder="Enter delivery time in minutes"
                              name="s_DeliveryTime"
                              min="0"
                            />
                          </div>
                        </div>
                      </div>

                      {/* Return Policy - Edit Mode */}
                      <div className="list-group-item">
                        <div className="d-flex align-items-center mb-2">
                          <i className="fas fa-undo text-secondary me-2"></i>
                          <span className="fw-semibold">Return Policy</span>
                        </div>
                        <div className="mt-2">
                          <textarea
                            className="form-control"
                            rows="4"
                            value={formState.s_ReturnPolicy}
                            onChange={(e) => {
                              setFormState((prev) => ({
                                ...prev,
                                s_ReturnPolicy: e.target.value,
                              }));
                            }}
                            placeholder="Enter return policy"
                            name="s_ReturnPolicy"
                          />
                        </div>
                      </div>

                      {/* Warning Settings - Edit Mode */}
                      {/* <div className="list-group-item bg-light">
                        <div className="d-flex align-items-center mb-3">
                          <i className="fas fa-exclamation-triangle me-2" style={{ color: themeColor }}></i>
                          <span className="fw-bold text-dark">Warning Settings</span>
                        </div>
                        <div className="row">
                          <div className="col-md-6"
                            <div className="d-flex align-items-center">
                              <div className="flex-fill">
                                <div className="d-flex align-items-center mb-2">
                                  <i className="fas fa-user-times me-2" style={{ color: themeColor }}></i>
                                  <span className="fw-semibold">Show Warning On Deleting Customer Data</span>
                                </div>
                                <div className="mt-2">
                                  <ToggleSwitch
                                    isChecked={formState.isShowWarningOnDeletingCustomerData}
                                    onToggle={(e) => {
                                      setFormState((prev) => ({
                                        ...prev,
                                        isShowWarningOnDeletingCustomerData: e.target.checked,
                                      }));
                                    }}
                                    label="Show Warning On Deleting Customer Data"
                                    name="isShowWarningOnDeletingCustomerData"
                                    themeColor={themeColor}
                                  />
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="d-flex align-items-center">
                              <div className="flex-fill">
                                <div className="d-flex align-items-center mb-2">
                                  <i className="fas fa-chair text-info me-2"></i>
                                  <span className="fw-semibold">Manage Covers of Table Plan</span>
                                </div>
                                <div className="mt-2">
                                  <ToggleSwitch
                                    isChecked={formState.manageCoversOnTablePlan}
                                    onToggle={(e) => {
                                      setFormState((prev) => ({
                                        ...prev,
                                        manageCoversOnTablePlan: e.target.checked,
                                      }));
                                    }}
                                    label="Manage Covers of Table Plan"
                                    name="manageCoversOnTablePlan"
                                    themeColor={themeColor}
                                  />
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div> */}

                      {/* Action Buttons */}
                      <div className="list-group-item bg-light border-top">
                        <div className="d-flex justify-content-end gap-2 mt-3">
                          <button
                            className="btn settings-btn settings-btn--ghost"
                            onClick={cancelGeneralEditing}
                            disabled={loading}
                          >
                            <i className="fas fa-times me-1"></i>
                            Cancel
                          </button>
                          <button
                            className="btn settings-btn settings-btn--primary"
                            style={{
                              backgroundColor: themeColor,
                              color: 'white',
                              border: 'none',
                              transition: 'all 0.3s ease'
                            }}
                            onClick={saveGeneralSettings}
                            disabled={loading}
                          >
                            {loading ? (
                              <>
                                <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                                Saving...
                              </>
                            ) : (
                              <>
                                <i className="fas fa-save me-1"></i>
                                Save Changes
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
            )}
            {activeSettingsTab === "vat" && renderTaxSection()}
            {activeSettingsTab === "reservation" && (
            <>
            <div id="TableReservation" className="settings-tab-panel">
              {renderSectionHeading(
                "Table Reservation",
                "Configure how reserved tables are shown and managed."
              )}
              <div className="card settings-section border-0">
                <div className="list-group list-group-flush">
                  {!isTableReservationEditing ? (
                    <>
                      {/* First Row - Margin Time and Release Time */}
                      <div className="list-group-item">
                        <div className="row g-3">
                          <div className="col-md-6" style={{ display: 'none' }}>
                            <div className="d-flex align-items-center mb-1">
                            <i className="fas fa-clock me-2" style={{ color: themeColor }}></i>
                              <span className="fw-semibold">Margin Time (min)</span>
                            </div>
                            <div className="text-muted">
                              <span className="badge bg-info">
                                {formState.s_TableReservationMarginTime ? `${formState.s_TableReservationMarginTime} minutes` : "Not set"}
                              </span>
                            </div>
                          </div>
                          <div className="col-md-6" style={{ display: 'none' }}>
                            <div className="d-flex align-items-center mb-1">
                            <i className="fas fa-hourglass-half me-2" style={{ color: themeColor }}></i>
                              <span className="fw-semibold">Release Time (min)</span>
                            </div>
                            <div className="text-muted">
                              <span className="badge bg-info">
                                {formState.s_TableReservedReleaseTime ? `${formState.s_TableReservedReleaseTime} minutes` : "Not set"}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Second Row - Reserved Table Status Change and Maximum Duration */}
                      <div className="list-group-item">
                        <div className="row g-3">
                          <div className="col-md-6">
                            <div className="d-flex align-items-center mb-1">
                            <i className="fas fa-exchange-alt me-2" style={{ color: themeColor }}></i>
                              <span className="fw-semibold">Alert Time Before Start Reservation (Min)</span>
                            </div>
                            <div className="text-muted">
                              <span className="badge bg-info">
                                {formState.s_ShowingReserveredTableTime ? `${formState.s_ShowingReserveredTableTime} minutes` : "Not set"}
                              </span>
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="d-flex align-items-center mb-1">
                            <i className="fas fa-hourglass-end me-2" style={{ color: themeColor }}></i>
                              <span className="fw-semibold">Maximum Reservation Duration (min)</span>
                            </div>
                            <div className="text-muted">
                              <span className="badge bg-info">
                                {formState.maximumReservationDuration ? `${formState.maximumReservationDuration} minutes` : "Not set"}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Single Edit Button */}
                      <div className="list-group-item d-flex justify-content-end">
                        <button
                          className="btn settings-btn settings-btn--ghost"
                          onClick={startTableReservationEditing}
                        >
                          Edit
                        </button>
                      </div>
                    </>
                  ) : (
                    <>
                      {/* First Row - Margin Time and Release Time */}
                      <div className="list-group-item">
                        <div className="row g-3">
                          <div className="col-md-6" style={{ display: 'none' }}>
                            <div className="d-flex align-items-center mb-2">
                              <i className="fas fa-clock text-info me-2"></i>
                              <span className="fw-semibold">Margin Time (min)</span>
                            </div>
                            <FormInput
                              type="number"
                              value={formState.s_TableReservationMarginTime}
                              onchange={(e) => {
                                const sanitizedValue = isNaN(Number(e.target.value)) || e.target.value === ""
                                  ? ""
                                  : Number(e.target.value);

                                if (typeof sanitizedValue === "number" && sanitizedValue < 0) {
                                  showErrorToast("Margin Time must be a positive number");
                                  return;
                                }

                                setFormState((prev) => ({
                                  ...prev,
                                  s_TableReservationMarginTime: sanitizedValue,
                                }));
                              }}
                              placeHolder="Enter margin time in minutes"
                              name="s_TableReservationMarginTime"
                              min="0"
                            />
                          </div>
                          <div className="col-md-6" style={{ display: 'none' }}>
                            <div className="d-flex align-items-center mb-2">
                              <i className="fas fa-hourglass-half me-2" style={{ color: themeColor }}></i>
                              <span className="fw-semibold">Release Time (min)</span>
                            </div>
                            <FormInput
                              type="number"
                              value={formState.s_TableReservedReleaseTime}
                              onchange={(e) => {
                                const sanitizedValue = isNaN(Number(e.target.value)) || e.target.value === ""
                                  ? ""
                                  : Number(e.target.value);

                                if (typeof sanitizedValue === "number" && sanitizedValue < 0) {
                                  showErrorToast("Release Time must be a positive number");
                                  return;
                                }

                                setFormState((prev) => ({
                                  ...prev,
                                  s_TableReservedReleaseTime: sanitizedValue,
                                }));
                              }}
                              placeHolder="Enter release time in minutes"
                              name="s_TableReservedReleaseTime"
                              min="0"
                            />
                          </div>
                        </div>
                      </div>

                      {/* Second Row - Reserved Table Status Change and Maximum Duration */}
                      <div className="list-group-item">
                        <div className="row g-3">
                          <div className="col-md-6">
                            <div className="d-flex align-items-center mb-2">
                              <i className="fas fa-exchange-alt me-2" style={{ color: themeColor }}></i>
                              <span className="fw-semibold">Alert Time Before Start Reservation (Min)</span>
                            </div>
                            <FormInput
                              type="number"
                              value={formState.s_ShowingReserveredTableTime}
                              onchange={(e) => {
                                const sanitizedValue = isNaN(Number(e.target.value)) || e.target.value === ""
                                  ? ""
                                  : Number(e.target.value);

                                if (typeof sanitizedValue === "number" && sanitizedValue < 0) {
                                  showErrorToast("Alert Time Before Start Reservation  must be a positive number");
                                  return;
                                }

                                setFormState((prev) => ({
                                  ...prev,
                                  s_ShowingReserveredTableTime: sanitizedValue,
                                }));
                              }}
                              placeHolder="Enter status change time in minutes"
                              name="s_ShowingReserveredTableTime"
                              min="0"
                            />
                          </div>
                          <div className="col-md-6" >
                            <div className="d-flex align-items-center mb-2">
                              <i className="fas fa-hourglass-end me-2" style={{ color: themeColor }}></i>
                              <span className="fw-semibold">Maximum Reservation Duration (min)</span>
                            </div>
                            <FormInput
                              type="number"
                              value={formState.maximumReservationDuration}
                              onchange={(e) => {
                                const sanitizedValue = isNaN(Number(e.target.value)) || e.target.value === ""
                                  ? ""
                                  : Number(e.target.value);

                                if (typeof sanitizedValue === "number" && sanitizedValue < 0) {
                                  showErrorToast("Maximum Reservation Duration must be a positive number");
                                  return;
                                }

                                setFormState((prev) => ({
                                  ...prev,
                                  maximumReservationDuration: sanitizedValue,
                                }));
                              }}
                              placeHolder="Enter maximum duration in minutes"
                              name="maximumReservationDuration"
                              min="0"
                            />
                          </div>
                        </div>
                      </div>

                      {/* Action Buttons */}
                      <div className="list-group-item bg-light border-top">
                        <div className="d-flex justify-content-end gap-2 mt-3">
                          <button
                            className="btn settings-btn settings-btn--ghost"
                            onClick={cancelTableReservationEditing}
                            disabled={loading}
                          >
                            <i className="fas fa-times me-1"></i>
                            Cancel
                          </button>
                          <button
                            className="btn settings-btn settings-btn--primary"
                            style={{
                              backgroundColor: themeColor,
                              color: 'white',
                              border: 'none',
                              transition: 'all 0.3s ease'
                            }}
                            onClick={saveTableReservationSettings}
                            disabled={loading}
                          >
                            {loading ? (
                              <>
                                <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                                Saving...
                              </>
                            ) : (
                              <>
                                <i className="fas fa-save me-1"></i>
                                Save Changes
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* <div id="Kitchen" className="mb-4 pb-3">
              <h4 className="d-flex align-items-center mb-2">
                <i className="fas fa-utensils me-2" style={{ color: themeColor }}></i>
                Kitchen
              </h4>
              <p>Manage kitchen-related settings.</p>
              <div className="card">
                <div className="list-group list-group-flush fw-bold">
                  {!isKitchenEditing ? (
                    <>
      
                      <div className="list-group-item d-flex align-items-center">
                        <div className="flex-fill">
                          <div className="d-flex align-items-center mb-1">
                            <i className="fas fa-sync me-2" style={{ color: themeColor }}></i>
                            <span className="fw-semibold">Update from Kitchen</span>
                          </div>
                          <div className="text-muted">
                            <span className={`badge ${formState.s_IsUpdatefromKitchen ? 'bg-success' : 'bg-secondary'}`}>
                              {formState.s_IsUpdatefromKitchen ? "Enabled" : "Disabled"}
                            </span>
                          </div>
                        </div>
                      </div>


                      <div className="list-group-item d-flex align-items-center">
                        <div className="flex-fill">
                          <div className="d-flex align-items-center mb-1">
                            <i className="fas fa-clock me-2" style={{ color: themeColor }}></i>
                            <span className="fw-semibold">Show Cancelled Order Duration (min)</span>
                          </div>
                          <div className="text-muted">
                            <span className="badge bg-success">
                              {formState.s_ShowCancelledOrderDuration ? `${formState.s_ShowCancelledOrderDuration} minutes` : "Not set"}
                            </span>
                          </div>
                        </div>
                      </div>

        
                      <div className="list-group-item d-flex align-items-center">
                        <div className="flex-fill">
                          <div className="d-flex align-items-center mb-1">
                            <i className="fas fa-calendar-times me-2" style={{ color: themeColor }}></i>
                            <span className="fw-semibold">Visible Cancel Order Days in Cancel Tab</span>
                          </div>
                          <div className="text-muted">
                            <span className="badge bg-success">
                              {formState.s_VisibleCancelOrderDaysinCancelTab ? `${formState.s_VisibleCancelOrderDaysinCancelTab} days` : "Not set"}
                            </span>
                          </div>
                        </div>
                      </div>

              
                      <div className="list-group-item d-flex justify-content-end">
                        <button
                          className="btn settings-btn settings-btn--ghost"
                          onClick={startKitchenEditing}
                        >
                          Edit
                        </button>
                      </div>
                    </>
                  ) : (
                    <>
                  
                      <div className="list-group-item">
                        <div className="d-flex align-items-center mb-2">
                          <i className="fas fa-sync text-info me-2"></i>
                          <span className="fw-semibold">Update from Kitchen</span>
                        </div>
                        <div className="mt-2">
                          <ToggleSwitch
                            isChecked={formState.s_IsUpdatefromKitchen}
                            onToggle={(e) => {
                              setFormState((prev) => ({
                                ...prev,
                                s_IsUpdatefromKitchen: e.target.checked,
                              }));
                            }}
                            label="Update from Kitchen"
                            name="s_IsUpdatefromKitchen"
                            themeColor={themeColor}
                          />
                        </div>
                      </div>

              
                      <div className="list-group-item">
                        <div className="d-flex align-items-center mb-2">
                            <i className="fas fa-clock me-2" style={{ color: themeColor }}></i>
                          <span className="fw-semibold">Show Cancelled Order Duration (min)</span>
                        </div>
                        <div className="mt-2">
                          <FormInput
                            type="number"
                            value={formState.s_ShowCancelledOrderDuration}
                            onchange={(e) => {
                              const sanitizedValue = isNaN(Number(e.target.value)) || e.target.value === ""
                                ? ""
                                : Number(e.target.value);

                              if (typeof sanitizedValue === "number" && sanitizedValue < 0) {
                                showErrorToast("Show Cancelled Order Duration must be a positive number");
                                return;
                              }

                              setFormState((prev) => ({
                                ...prev,
                                s_ShowCancelledOrderDuration: sanitizedValue,
                              }));
                            }}
                            placeHolder="Enter duration in minutes"
                            name="s_ShowCancelledOrderDuration"
                            min="0"
                          />
                        </div>
                      </div>

                      <div className="list-group-item">
                        <div className="d-flex align-items-center mb-2">
                            <i className="fas fa-calendar-times me-2" style={{ color: themeColor }}></i>
                          <span className="fw-semibold">Visible Cancel Order Days in Cancel Tab</span>
                        </div>
                        <div className="mt-2">
                          <FormInput
                            type="number"
                            value={formState.s_VisibleCancelOrderDaysinCancelTab}
                            onchange={(e) => {
                              const sanitizedValue = isNaN(Number(e.target.value)) || e.target.value === ""
                                ? ""
                                : Number(e.target.value);

                              if (typeof sanitizedValue === "number" && sanitizedValue < 0) {
                                showErrorToast("Visible Cancel Order Days must be a positive number");
                                return;
                              }

                              setFormState((prev) => ({
                                ...prev,
                                s_VisibleCancelOrderDaysinCancelTab: sanitizedValue,
                              }));
                            }}
                            placeHolder="Enter number of days"
                            name="s_VisibleCancelOrderDaysinCancelTab"
                            min="0"
                          />
                        </div>
                      </div>

   
                      <div className="list-group-item bg-light border-top">
                        <div className="d-flex justify-content-end gap-2 mt-3">
                          <button
                            className="btn settings-btn settings-btn--ghost"
                            onClick={cancelKitchenEditing}
                            disabled={loading}
                          >
                            <i className="fas fa-times me-1"></i>
                            Cancel
                          </button>
                          <button
                            className="btn settings-btn settings-btn--primary"
                            style={{
                              backgroundColor: themeColor,
                              color: 'white',
                              border: 'none',
                              transition: 'all 0.3s ease'
                            }}
                            onClick={saveKitchenSettings}
                            disabled={loading}
                          >
                            {loading ? (
                              <>
                                <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                                Saving...
                              </>
                            ) : (
                              <>
                                <i className="fas fa-save me-1"></i>
                                Save Changes
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div> */}

            {/* Orders Tracking TV - card commented out
            <div id="ChooseModeforOrdersTrackingTVApp" className="mb-4 pb-3">
              <h4 className="d-flex align-items-center mb-2">
                <i className="fas fa-tv me-2" style={{ color: themeColor }}></i>
                Orders Tracking TV
              </h4>
              <p>Choose modes for the orders tracking TV app.</p>
              <div className="card">
                <div className="list-group list-group-flush fw-bold">
                  {!isTVAppEditing ? (
                    <>
                      <div className="list-group-item d-flex align-items-center">
                        <div className="flex-fill">
                          <div className="d-flex align-items-center mb-1">
                            <i className="fas fa-info-circle me-2" style={{ color: themeColor }}></i>
                            <span className="fw-semibold">Order Status</span>
                          </div>
                          <div className="text-muted">
                            <span className={`badge ${formState.tV_ISOrderStatus ? 'bg-success' : 'bg-secondary'}`}>
                              {formState.tV_ISOrderStatus ? "Enabled" : "Disabled"}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="list-group-item d-flex align-items-center">
                        <div className="flex-fill">
                          <div className="d-flex align-items-center mb-1">
                            <i className="fas fa-tags me-2" style={{ color: themeColor }}></i>
                            <span className="fw-semibold">Order Type</span>
                          </div>
                          <div className="text-muted">
                            <span className={`badge ${formState.tV_ISOrderType ? 'bg-success' : 'bg-secondary'}`}>
                              {formState.tV_ISOrderType ? "Enabled" : "Disabled"}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="list-group-item d-flex align-items-center">
                        <div className="flex-fill">
                          <div className="d-flex align-items-center mb-1">
                            <i className="fas fa-hashtag me-2" style={{ color: themeColor }}></i>
                            <span className="fw-semibold">Order Number</span>
                          </div>
                          <div className="text-muted">
                            <span className={`badge ${formState.tV_ISOrderNumber ? 'bg-success' : 'bg-secondary'}`}>
                              {formState.tV_ISOrderNumber ? "Enabled" : "Disabled"}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="list-group-item d-flex align-items-center">
                        <div className="flex-fill">
                          <div className="d-flex align-items-center mb-1">
                            <i className="fas fa-user me-2" style={{ color: themeColor }}></i>
                            <span className="fw-semibold">Order Owner</span>
                          </div>
                          <div className="text-muted">
                            <span className={`badge ${formState.tV_ISOrderOwner ? 'bg-success' : 'bg-secondary'}`}>
                              {formState.tV_ISOrderOwner ? "Enabled" : "Disabled"}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="list-group-item d-flex justify-content-end">
                        <button
                          className="btn settings-btn settings-btn--ghost"
                          onClick={startTVAppEditing}
                        >
                          Edit
                        </button>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="list-group-item">
                        <div className="d-flex align-items-center mb-2">
                          <i className="fas fa-info-circle me-2" style={{ color: themeColor }}></i>
                          <span className="fw-semibold">Order Status</span>
                        </div>
                        <div className="mt-2">
                          <ToggleSwitch
                            isChecked={formState.tV_ISOrderStatus}
                            onToggle={(checked) => {
                              setFormState((prev) => ({
                                ...prev,
                                tV_ISOrderStatus: checked,
                              }));
                            }}
                            label="Order Status"
                            name="tV_ISOrderStatus"
                            themeColor={themeColor}
                          />
                        </div>
                      </div>
                      <div className="list-group-item">
                        <div className="d-flex align-items-center mb-2">
                          <i className="fas fa-tags me-2" style={{ color: themeColor }}></i>
                          <span className="fw-semibold">Order Type</span>
                        </div>
                        <div className="mt-2">
                          <ToggleSwitch
                            isChecked={formState.tV_ISOrderType}
                            onToggle={(checked) => {
                              setFormState((prev) => ({
                                ...prev,
                                tV_ISOrderType: checked,
                              }));
                            }}
                            label="Order Type"
                            name="tV_ISOrderType"
                            themeColor={themeColor}
                          />
                        </div>
                      </div>
                      <div className="list-group-item">
                        <div className="d-flex align-items-center mb-2">
                          <i className="fas fa-hashtag me-2" style={{ color: themeColor }}></i>
                          <span className="fw-semibold">Order Number</span>
                        </div>
                        <div className="mt-2">
                          <ToggleSwitch
                            isChecked={formState.tV_ISOrderNumber}
                            onToggle={(checked) => {
                              setFormState((prev) => ({
                                ...prev,
                                tV_ISOrderNumber: checked,
                              }));
                            }}
                            label="Order Number"
                            name="tV_ISOrderNumber"
                            themeColor={themeColor}
                          />
                        </div>
                      </div>
                      <div className="list-group-item">
                        <div className="d-flex align-items-center mb-2">
                          <i className="fas fa-user me-2" style={{ color: themeColor }}></i>
                          <span className="fw-semibold">Order Owner</span>
                        </div>
                        <div className="mt-2">
                          <ToggleSwitch
                            isChecked={formState.tV_ISOrderOwner}
                            onToggle={(checked) => {
                              setFormState((prev) => ({
                                ...prev,
                                tV_ISOrderOwner: checked,
                              }));
                            }}
                            label="Order Owner"
                            name="tV_ISOrderOwner"
                            themeColor={themeColor}
                          />
                        </div>
                      </div>
                      <div className="list-group-item bg-light border-top">
                        <div className="d-flex justify-content-end gap-2 mt-3">
                          <button
                            className="btn settings-btn settings-btn--ghost"
                            onClick={cancelTVAppEditing}
                            disabled={loading}
                          >
                            <i className="fas fa-times me-1"></i>
                            Cancel
                          </button>
                          <button
                            className="btn settings-btn settings-btn--primary"
                            style={{
                              backgroundColor: themeColor,
                              color: 'white',
                              border: 'none',
                              transition: 'all 0.3s ease'
                            }}
                            onClick={saveTVAppSettings}
                            disabled={loading}
                          >
                            {loading ? (
                              <>
                                <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                                Saving...
                              </>
                            ) : (
                              <>
                                <i className="fas fa-save me-1"></i>
                                Save Changes
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
            */}
            </>
            )}
            {activeSettingsTab === "businessTime" && (
           <div id="ServiceHours" className="settings-tab-panel">
              {renderSectionHeading(
                "Service Hours",
                "Manage restaurant, delivery, and collection hours for your business."
              )}

              {/* Restaurant Hours Card */}
              <div className="card settings-section border-0 mb-3">
                {editingField !== "weeklyServiceHours" ? (
                  <div className="card-body">
                    <div className="d-flex align-items-center mb-3">
                      <div className="flex-fill">
                        <div className="d-flex align-items-center mb-2">
                          <i className="fas fa-utensils me-2" style={{ color: themeColor }}></i>
                          <h5 className="mb-0 fw-semibold">Restaurant Hours</h5>
                        </div>
                        {renderServiceHoursDisplay(
                          "weeklyServiceHours",
                          timingData.weeklyServiceHours,
                          "Restaurant Hours"
                        )}
                      </div>
                    </div>
                    <div className="d-flex justify-content-end">
                      <button
                        className="btn settings-btn settings-btn--ghost"
                        onClick={() => startEditing("weeklyServiceHours", timingData.weeklyServiceHours)}
                      >
                        Edit
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="card-body">
                    <div className="card"
                      style={{ borderColor: themeColor, borderWidth: '2px' }}>
                      <div className="card-body">
                        <div className="d-flex align-items-center mb-3">
                          <i className="fas fa-utensils me-2" style={{ color: themeColor }}></i>
                          <h5 className="mb-0 fw-semibold">Edit Restaurant Hours</h5>
                        </div>

                        <div className="mb-4">
                          <ToggleSwitch
                            isChecked={enableSchedule.weeklyServiceHours}
                            onToggle={() =>
                              setEnableSchedule((prev) => ({
                                ...prev,
                                weeklyServiceHours: !prev.weeklyServiceHours,
                              }))
                            }
                            label="Set Restaurant Hours"
                            name="enableSchedule-weeklyServiceHours"
                            themeColor={themeColor}
                          />
                          <p className="text-muted mt-2">
                            Set the days of the week and times of day for restaurant hours.
                          </p>
                        </div>

                        {enableSchedule.weeklyServiceHours && (
                          <div className="mb-4">
                            <div className="row">
                              <div className="col-12">
                                <ScheduleManager
                                  schedules={schedules.weeklyServiceHours}
                                  setSchedules={(updatedSchedules) =>
                                    setSchedules((prev) => ({
                                      ...prev,
                                      weeklyServiceHours:
                                        typeof updatedSchedules === "function"
                                          ? updatedSchedules(prev.weeklyServiceHours)
                                          : updatedSchedules,
                                    }))
                                  }
                                  isProfilePage={true}
                                  isDarkMode={isDarkMode}
                                  themeColor={themeColor}
                                />
                              </div>
                            </div>
                          </div>
                        )}

                        <div className="d-flex justify-content-end gap-2 mt-3">
                          <button
                            className="btn settings-btn settings-btn--ghost"
                            onClick={cancelEditing}
                            disabled={loading}
                          >
                            <i className="fas fa-times me-1"></i>
                            Cancel
                          </button>
                          <button
                            className="btn settings-btn settings-btn--primary"
                            style={{
                              backgroundColor: themeColor,
                              color: 'white',
                              border: 'none',
                              transition: 'all 0.3s ease'
                            }}
                            onClick={() => saveField("weeklyServiceHours")}
                            disabled={loading}
                          >
                            {loading ? (
                              <>
                                <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                                Saving...
                              </>
                            ) : (
                              <>
                                <i className="fas fa-save me-1"></i>
                                Save Changes
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Delivery Time Card */}
              <div className="card settings-section border-0 mb-3">
                {editingField !== "DeliveryTime" ? (
                  <div className="card-body">
                    <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
                      <div className="d-flex align-items-center">
                        <i className="fas fa-truck me-2" style={{ color: themeColor }}></i>
                        <h5 className="mb-0 fw-semibold">Delivery Time</h5>
                      </div>
                      <ToggleSwitch
                        isChecked={formState.compDeliveryStatus}
                        onToggle={(checked) => {
                          saveOrderingBooleans({ CompDeliveryStatus: checked });
                        }}
                        label={formState.compDeliveryStatus ? "Delivery on" : "Delivery off"}
                        name="compDeliveryStatus-hours"
                        themeColor={themeColor}
                      />
                    </div>
                    {renderServiceHoursDisplay(
                      "DeliveryTime",
                      timingData.DeliveryTime,
                      "Delivery Time"
                    )}
                    <div className="d-flex justify-content-end mt-3">
                      <button
                        className="btn settings-btn settings-btn--ghost"
                        onClick={() => startEditing("DeliveryTime", timingData.DeliveryTime)}
                      >
                        Edit
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="card-body">
                    <div className="card"
                      style={{ borderColor: themeColor, borderWidth: '2px' }}>
                      <div className="card-body">
                        <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
                          <div className="d-flex align-items-center">
                            <i className="fas fa-truck me-2" style={{ color: themeColor }}></i>
                            <h5 className="mb-0 fw-semibold">Edit Delivery Time</h5>
                          </div>
                          <ToggleSwitch
                            isChecked={formState.compDeliveryStatus}
                            onToggle={(checked) => {
                              saveOrderingBooleans({ CompDeliveryStatus: checked });
                            }}
                            label={formState.compDeliveryStatus ? "Delivery on" : "Delivery off"}
                            name="compDeliveryStatus-edit"
                            themeColor={themeColor}
                          />
                        </div>

                        <div className="mb-4">
                          <ToggleSwitch
                            isChecked={enableSchedule.DeliveryTime}
                            onToggle={() =>
                              setEnableSchedule((prev) => ({
                                ...prev,
                                DeliveryTime: !prev.DeliveryTime,
                              }))
                            }
                            label="Set Delivery Time"
                            name="enableSchedule-DeliveryTime"
                            themeColor={themeColor}
                          />
                          <p className="text-muted mt-2">
                            Set delivery hours. Delivery on/off does not remove these times. Online ordering email/phone are managed under Online Ordering.
                          </p>
                        </div>

                        {enableSchedule.DeliveryTime && (
                          <div className="mb-4">
                            <div className="row">
                              <div className="col-12">
                                <ScheduleManager
                                  schedules={schedules.DeliveryTime}
                                  setSchedules={(updatedSchedules) =>
                                    setSchedules((prev) => ({
                                      ...prev,
                                      DeliveryTime:
                                        typeof updatedSchedules === "function"
                                          ? updatedSchedules(prev.DeliveryTime)
                                          : updatedSchedules,
                                    }))
                                  }
                                  isProfilePage={true}
                                  isDarkMode={isDarkMode}
                                  themeColor={themeColor}
                                />
                              </div>
                            </div>
                          </div>
                        )}

                        <div className="d-flex justify-content-end gap-2 mt-3">
                          <button
                            className="btn settings-btn settings-btn--ghost"
                            onClick={cancelEditing}
                            disabled={loading}
                          >
                            <i className="fas fa-times me-1"></i>
                            Cancel
                          </button>
                          <button
                            className="btn settings-btn settings-btn--primary"
                            style={{
                              backgroundColor: themeColor,
                              color: 'white',
                              border: 'none',
                              transition: 'all 0.3s ease'
                            }}
                            onClick={() => saveField("DeliveryTime")}
                            disabled={loading}
                          >
                            {loading ? (
                              <>
                                <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                                Saving...
                              </>
                            ) : (
                              <>
                                <i className="fas fa-save me-1"></i>
                                Save Changes
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Collection Time Card */}
              <div className="card settings-section border-0 mb-3">
                {editingField !== "CollectionTime" ? (
                  <div className="card-body">
                    <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
                      <div className="d-flex align-items-center">
                        <i className="fas fa-hand-holding me-2" style={{ color: themeColor }}></i>
                        <h5 className="mb-0 fw-semibold">Collection Time</h5>
                      </div>
                      <ToggleSwitch
                        isChecked={formState.compCollectionStatus}
                        onToggle={(checked) => {
                          saveOrderingBooleans({ CompCollectionStatus: checked });
                        }}
                        label={formState.compCollectionStatus ? "Collection on" : "Collection off"}
                        name="compCollectionStatus-hours"
                        themeColor={themeColor}
                      />
                    </div>
                    {renderServiceHoursDisplay(
                      "CollectionTime",
                      timingData.CollectionTime,
                      "Collection Time"
                    )}
                    <div className="d-flex justify-content-end mt-3">
                      <button
                        className="btn settings-btn settings-btn--ghost"
                        onClick={() => startEditing("CollectionTime", timingData.CollectionTime)}
                      >
                        Edit
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="card-body">
                    <div className="card"
                      style={{ borderColor: themeColor, borderWidth: '2px' }}>
                      <div className="card-body">
                        <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
                          <div className="d-flex align-items-center">
                            <i className="fas fa-hand-holding me-2" style={{ color: themeColor }}></i>
                            <h5 className="mb-0 fw-semibold">Edit Collection Time</h5>
                          </div>
                          <ToggleSwitch
                            isChecked={formState.compCollectionStatus}
                            onToggle={(checked) => {
                              saveOrderingBooleans({ CompCollectionStatus: checked });
                            }}
                            label={formState.compCollectionStatus ? "Collection on" : "Collection off"}
                            name="compCollectionStatus-edit"
                            themeColor={themeColor}
                          />
                        </div>

                        <div className="mb-4">
                          <ToggleSwitch
                            isChecked={enableSchedule.CollectionTime}
                            onToggle={() =>
                              setEnableSchedule((prev) => ({
                                ...prev,
                                CollectionTime: !prev.CollectionTime,
                              }))
                            }
                            label="Set Collection Time"
                            name="enableSchedule-CollectionTime"
                            themeColor={themeColor}
                          />
                          <p className="text-muted mt-2">
                            Set collection hours. Collection on/off does not remove these times. Online ordering email/phone are managed under Online Ordering.
                          </p>
                        </div>

                        {enableSchedule.CollectionTime && (
                          <div className="mb-4">
                            <div className="row">
                              <div className="col-12">
                                <ScheduleManager
                                  schedules={schedules.CollectionTime}
                                  setSchedules={(updatedSchedules) =>
                                    setSchedules((prev) => ({
                                      ...prev,
                                      CollectionTime:
                                        typeof updatedSchedules === "function"
                                          ? updatedSchedules(prev.CollectionTime)
                                          : updatedSchedules,
                                    }))
                                  }
                                  isProfilePage={true}
                                  isDarkMode={isDarkMode}
                                  themeColor={themeColor}
                                />
                              </div>
                            </div>
                          </div>
                        )}

                        <div className="d-flex justify-content-end gap-2 mt-3">
                          <button
                            className="btn settings-btn settings-btn--ghost"
                            onClick={cancelEditing}
                            disabled={loading}
                          >
                            <i className="fas fa-times me-1"></i>
                            Cancel
                          </button>
                          <button
                            className="btn settings-btn settings-btn--primary"
                            style={{
                              backgroundColor: themeColor,
                              color: 'white',
                              border: 'none',
                              transition: 'all 0.3s ease'
                            }}
                            onClick={() => saveField("CollectionTime")}
                            disabled={loading}
                          >
                            {loading ? (
                              <>
                                <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                                Saving...
                              </>
                            ) : (
                              <>
                                <i className="fas fa-save me-1"></i>
                                Save Changes
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
            )}
            {activeSettingsTab === "onlineOrdering" && (
            <div id="OnlineOrderingModule" className="settings-tab-panel">
              {renderSectionHeading(
                "Online Ordering",
                "Collection and delivery channels for online ordering, order rules, and dedicated contact details."
              )}
              <div className="card settings-section border-0">
                <div className="list-group list-group-flush">
                  {!isOnlineOrderingEditing ? (
                    <>
                      <div className="list-group-item">
                        <div className="d-flex align-items-center mb-3">
                          <i className="fas fa-cogs me-2" style={{ color: themeColor }}></i>
                          <span className="fw-bold">Ordering channels</span>
                        </div>
                        <p className="text-muted small mb-3">
                          Turn collection and delivery on or off for online ordering.
                        </p>
                        <div className="row">
                          <div className="col-md-6">
                            <div className="d-flex align-items-center mb-1">
                              <i className="fas fa-store me-2" style={{ color: themeColor }}></i>
                              <span className="fw-semibold">Collection</span>
                            </div>
                            <div className="text-muted">
                              <span className="badge bg-secondary">
                                {formState.compCollectionStatus ? "Active" : "Inactive"}
                              </span>
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="d-flex align-items-center mb-1">
                              <i className="fas fa-shipping-fast me-2" style={{ color: themeColor }}></i>
                              <span className="fw-semibold">Delivery</span>
                            </div>
                            <div className="text-muted">
                              <span className="badge bg-secondary">
                                {formState.compDeliveryStatus ? "Active" : "Inactive"}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="row mt-3">
                          <div className="col-md-6">
                            <div className="d-flex align-items-center mb-1">
                              <i className="fas fa-credit-card me-2" style={{ color: themeColor }}></i>
                              <span className="fw-semibold">Collection online payment</span>
                            </div>
                            <div className="text-muted">
                              <span className="badge bg-secondary">
                                {formState.s_OnlineOrderingCollectionPayment
                                  ? "Pay online"
                                  : "Pay at collection"}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="list-group-item">
                        <div className="row g-3">
                          <div className="col-md-6">
                            <div className="d-flex align-items-center mb-1">
                              <i className="fas fa-money-bill-wave me-2" style={{ color: themeColor }}></i>
                              <span className="fw-semibold">Minimum Order Amount</span>
                            </div>
                            <div className="text-muted">
                              <span className="badge bg-info">
                                {formState.s_MinOrderAmount ? `${currencySymbol}${formState.s_MinOrderAmount}` : "Not set"}
                              </span>
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="d-flex align-items-center mb-1">
                              <i className="fas fa-clock me-2" style={{ color: themeColor }}></i>
                              <span className="fw-semibold">Delivery Time (min)</span>
                            </div>
                            <div className="text-muted">
                              <span className="badge bg-info">
                                {formState.s_DeliveryTime ? `${formState.s_DeliveryTime} minutes` : "Not set"}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="list-group-item">
                        <div className="row g-3">
                          <div className="col-md-6">
                            <div className="d-flex align-items-center mb-1">
                              <i className="fas fa-envelope me-2" style={{ color: themeColor }}></i>
                              <span className="fw-semibold">Online Ordering Email</span>
                            </div>
                            <div className="text-muted">
                              {formState.s_OnlineOrderingEmail || "Not set"}
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="d-flex align-items-center mb-1">
                              <i className="fas fa-phone me-2" style={{ color: themeColor }}></i>
                              <span className="fw-semibold">Online Ordering Phone</span>
                            </div>
                            <div className="text-muted">
                              {formState.s_OnlineOrderingPhone || "Not set"}
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="list-group-item d-flex justify-content-end">
                        <button
                          className="btn settings-btn settings-btn--ghost"
                          onClick={startOnlineOrderingEditing}
                        >
                          Edit
                        </button>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="list-group-item">
                        <div className="d-flex align-items-center mb-3">
                          <i className="fas fa-cogs me-2" style={{ color: themeColor }}></i>
                          <span className="fw-bold">Ordering channels</span>
                        </div>
                        <p className="text-muted small mb-3">
                          Turn collection and delivery on or off for online ordering.
                        </p>
                        <div className="row">
                          <div className="col-md-6">
                            <div className="d-flex align-items-center mb-2">
                              <i className="fas fa-store me-2" style={{ color: themeColor }}></i>
                              <span className="fw-semibold">Collection</span>
                            </div>
                            <ToggleSwitch
                              isChecked={formState.compCollectionStatus}
                              onToggle={(checked) => {
                                setFormState((prev) => ({
                                  ...prev,
                                  compCollectionStatus: checked,
                                }));
                              }}
                              label="Collection active"
                              name="compCollectionStatus"
                              themeColor={themeColor}
                            />
                          </div>
                          <div className="col-md-6">
                            <div className="d-flex align-items-center mb-2">
                              <i className="fas fa-shipping-fast me-2" style={{ color: themeColor }}></i>
                              <span className="fw-semibold">Delivery</span>
                            </div>
                            <ToggleSwitch
                              isChecked={formState.compDeliveryStatus}
                              onToggle={(checked) => {
                                setFormState((prev) => ({
                                  ...prev,
                                  compDeliveryStatus: checked,
                                }));
                              }}
                              label="Delivery active"
                              name="compDeliveryStatus"
                              themeColor={themeColor}
                            />
                          </div>
                        </div>
                        <div className="row mt-3">
                          <div className="col-md-6">
                            <div className="d-flex align-items-center mb-2">
                              <i className="fas fa-credit-card me-2" style={{ color: themeColor }}></i>
                              <span className="fw-semibold">Collection online payment</span>
                            </div>
                            <p className="text-muted small mb-2">
                              When off, collection orders are placed unpaid for payment at the counter.
                            </p>
                            <ToggleSwitch
                              isChecked={formState.s_OnlineOrderingCollectionPayment}
                              onToggle={(checked) => {
                                setFormState((prev) => ({
                                  ...prev,
                                  s_OnlineOrderingCollectionPayment: checked,
                                }));
                              }}
                              label={
                                formState.s_OnlineOrderingCollectionPayment
                                  ? "Pay online"
                                  : "Pay at collection"
                              }
                              name="s_OnlineOrderingCollectionPayment"
                              themeColor={themeColor}
                            />
                          </div>
                        </div>
                      </div>

                      <div className="list-group-item">
                        <div className="row g-3">
                          <div className="col-md-6">
                            <div className="d-flex align-items-center mb-2">
                              <i className="fas fa-money-bill-wave me-2" style={{ color: themeColor }}></i>
                              <span className="fw-semibold">Minimum Order Amount</span>
                            </div>
                            <FormInput
                              type="number"
                              value={formState.s_MinOrderAmount}
                              onchange={(e) => {
                                const sanitizedValue = isNaN(Number(e.target.value)) || e.target.value === ""
                                  ? ""
                                  : Number(e.target.value);
                                if (typeof sanitizedValue === "number" && sanitizedValue < 0) {
                                  showErrorToast("Minimum Order Amount must be a positive number");
                                  return;
                                }
                                setFormState((prev) => ({
                                  ...prev,
                                  s_MinOrderAmount: sanitizedValue,
                                }));
                              }}
                              placeHolder={`${currencySymbol}0.00`}
                              name="s_MinOrderAmount"
                              prepend={currencySymbol}
                            />
                          </div>
                          <div className="col-md-6">
                            <div className="d-flex align-items-center mb-2">
                              <i className="fas fa-clock me-2" style={{ color: themeColor }}></i>
                              <span className="fw-semibold">Delivery Time (min)</span>
                            </div>
                            <FormInput
                              type="number"
                              value={formState.s_DeliveryTime}
                              onchange={(e) => {
                                const sanitizedValue = isNaN(Number(e.target.value)) || e.target.value === ""
                                  ? ""
                                  : Number(e.target.value);
                                if (typeof sanitizedValue === "number" && sanitizedValue < 0) {
                                  showErrorToast("Delivery Time must be a positive number");
                                  return;
                                }
                                setFormState((prev) => ({
                                  ...prev,
                                  s_DeliveryTime: sanitizedValue,
                                }));
                              }}
                              placeHolder="e.g. 45"
                              name="s_DeliveryTime"
                            />
                          </div>
                        </div>
                      </div>

                      <div className="list-group-item">
                        <div className="row g-3">
                          <div className="col-md-6">
                            <div className="d-flex align-items-center mb-2">
                              <i className="fas fa-envelope me-2" style={{ color: themeColor }}></i>
                              <span className="fw-semibold">Online Ordering Email</span>
                            </div>
                            <FormInput
                              type="email"
                              value={formState.s_OnlineOrderingEmail}
                              onchange={(e) => {
                                setFormState((prev) => ({
                                  ...prev,
                                  s_OnlineOrderingEmail: e.target.value,
                                }));
                              }}
                              placeHolder="orders@restaurant.com"
                              name="s_OnlineOrderingEmail"
                            />
                          </div>
                          <div className="col-md-6">
                            <div className="d-flex align-items-center mb-2">
                              <i className="fas fa-phone me-2" style={{ color: themeColor }}></i>
                              <span className="fw-semibold">Online Ordering Phone</span>
                            </div>
                            <FormInput
                              type="text"
                              value={formState.s_OnlineOrderingPhone}
                              onchange={(e) => {
                                setFormState((prev) => ({
                                  ...prev,
                                  s_OnlineOrderingPhone: e.target.value,
                                }));
                              }}
                              placeHolder="+44..."
                              name="s_OnlineOrderingPhone"
                            />
                          </div>
                        </div>
                      </div>

                      <div className="list-group-item bg-light border-top">
                        <div className="d-flex justify-content-end gap-2 mt-3">
                          <button
                            className="btn settings-btn settings-btn--ghost"
                            onClick={cancelOnlineOrderingEditing}
                            disabled={loading}
                          >
                            <i className="fas fa-times me-1"></i>
                            Cancel
                          </button>
                          <button
                            className="btn settings-btn settings-btn--primary"
                            style={{
                              backgroundColor: themeColor,
                              color: 'white',
                              border: 'none',
                              transition: 'all 0.3s ease'
                            }}
                            onClick={saveOnlineOrderingSettings}
                            disabled={loading}
                          >
                            {loading ? (
                              <>
                                <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                                Saving...
                              </>
                            ) : (
                              <>
                                <i className="fas fa-save me-1"></i>
                                Save Changes
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>

              <CollectionQrSection
                compId={CompID}
                collectionEnabled={Boolean(formState.compCollectionStatus)}
                themeColor={themeColor}
              />
            </div>
            )}
            {activeSettingsTab === "preOrdering" && (
            <div id="PreOrderingModule" className="settings-tab-panel">
              {renderSectionHeading(
                "Pre-Ordering",
                "Configure pre-ordering options for online orders."
              )}
              <div className="card settings-section border-0">
                <div className="list-group list-group-flush">
                  {!isPreOrderingEditing ? (
                    <>
                      <div className="list-group-item d-flex align-items-center">
                        <div className="flex-fill">
                          <div>Pre-Ordering</div>
                          <div className="text-muted">
                            {formState.isActivePreOrderModule ? "Enabled" : "Disabled"}
                          </div>
                        </div>
                      </div>
                      {formState.isActivePreOrderModule && (
                        <div className="list-group-item">
                          <div className="row">
                            <div className="col-md-6">
                              <div className="d-flex align-items-center mb-1">
                                <i className="fas fa-shopping-cart me-2" style={{ color: themeColor }}></i>
                                <span className="fw-semibold">Pre-Ordering Collection</span>
                              </div>
                              <div className="text-muted">
                                <span className={`badge ${formState.preOrderingCollection ? 'bg-success' : 'bg-secondary'}`}>
                                  {formState.preOrderingCollection ? "Enabled" : "Disabled"}
                                </span>
                              </div>
                            </div>
                            <div className="col-md-6">
                              <div className="d-flex align-items-center mb-1">
                                <i className="fas fa-truck me-2" style={{ color: themeColor }}></i>
                                <span className="fw-semibold">Pre-Ordering Delivery</span>
                              </div>
                              <div className="text-muted">
                                <span className={`badge ${formState.preOrderingDelivery ? 'bg-success' : 'bg-secondary'}`}>
                                  {formState.preOrderingDelivery ? "Enabled" : "Disabled"}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                      {formState.isActivePreOrderModule && (
                        <div className="list-group-item d-flex align-items-center">
                          <div className="flex-fill">
                            <div className="d-flex align-items-center mb-1">
                              <i className="fas fa-calendar-alt me-2" style={{ color: themeColor }}></i>
                              <span className="fw-semibold">Pre-Order Day Range</span>
                            </div>
                            <div className="text-muted">
                              <span className="badge bg-success">
                                {preOrderOptions.find(opt => opt.value === formState.preOrderDayRange)?.label || "Not set"}
                              </span>
                            </div>
                          </div>
                        </div>
                      )}
                      {formState.isActivePreOrderModule && (
                        <div className="list-group-item d-flex align-items-center">
                          <div className="flex-fill">
                            <div className="d-flex align-items-center mb-1">
                              <i className="fas fa-clock me-2" style={{ color: themeColor }}></i>
                              <span className="fw-semibold">Pre Order Alert within Minutes</span>
                            </div>
                            <div className="text-muted">
                              <span className="badge bg-success">
                                {formState.preOrderAlertInMins ? `${formState.preOrderAlertInMins} minutes` : "Not set"}
                              </span>
                            </div>
                          </div>
                        </div>
                      )}
                      <div className="list-group-item d-flex justify-content-end">
                        <button
                          className="btn settings-btn settings-btn--ghost"
                          onClick={startPreOrderingEditing}
                        >
                          Edit
                        </button>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="list-group-item">
                        <div className="d-flex align-items-center mb-2">
                          <i className="fas fa-shopping-bag me-2" style={{ color: themeColor }}></i>
                          <span className="fw-semibold">Pre-Ordering</span>
                        </div>
                        <ToggleSwitch
                          isChecked={formState.isActivePreOrderModule}
                          onToggle={(checked) => {
                            setFormState((prev) => ({
                              ...prev,
                              isActivePreOrderModule: checked,
                            }));
                          }}
                          label="Pre-Ordering"
                          name="isActivePreOrderModule"
                          themeColor={themeColor}
                        />
                      </div>
                      {formState.isActivePreOrderModule && (
                        <div className="list-group-item">
                          <div className="row">
                            <div className="col-md-6">
                              <div className="d-flex align-items-center mb-2">
                                <i className="fas fa-shopping-cart me-2" style={{ color: themeColor }}></i>
                                <span className="fw-semibold">Pre-Ordering Collection</span>
                              </div>
                              <ToggleSwitch
                                isChecked={formState.preOrderingCollection}
                                onToggle={(checked) => {
                                  setFormState((prev) => ({
                                    ...prev,
                                    preOrderingCollection: checked,
                                  }));
                                }}
                                label="Collection"
                                name="preOrderingCollection"
                                themeColor={themeColor}
                              />
                            </div>
                            <div className="col-md-6">
                              <div className="d-flex align-items-center mb-2">
                                <i className="fas fa-truck me-2" style={{ color: themeColor }}></i>
                                <span className="fw-semibold">Pre-Ordering Delivery</span>
                              </div>
                              <ToggleSwitch
                                isChecked={formState.preOrderingDelivery}
                                onToggle={(checked) => {
                                  setFormState((prev) => ({
                                    ...prev,
                                    preOrderingDelivery: checked,
                                  }));
                                }}
                                label="Delivery"
                                name="preOrderingDelivery"
                                themeColor={themeColor}
                              />
                            </div>
                          </div>
                        </div>
                      )}
                      {formState.isActivePreOrderModule && (
                        <div className="list-group-item">
                          <div className="d-flex align-items-center mb-2">
                            <i className="fas fa-calendar-alt me-2" style={{ color: themeColor }}></i>
                            <span className="fw-semibold">Pre-Order Day Range</span>
                          </div>
                          <Select
                            options={preOrderOptions}
                            value={preOrderOptions.find((o) => o.value === formState.preOrderDayRange)}
                            onChange={(option) => {
                              setFormState((prev) => ({
                                ...prev,
                                preOrderDayRange: option ? option.value : "",
                              }));
                            }}
                            placeholder="Select Pre-Order Day Range"
                            isClearable
                            styles={dropdownStyles}
                            className="react-select-container"
                            classNamePrefix="react-select"
                          />
                        </div>
                      )}
                      {formState.isActivePreOrderModule && (
                        <div className="list-group-item">
                          <div className="d-flex align-items-center mb-2">
                            <i className="fas fa-clock me-2" style={{ color: themeColor }}></i>
                            <span className="fw-semibold">Pre Order Alert within Minutes</span>
                          </div>
                          <FormInput
                            type="number"
                            value={formState.preOrderAlertInMins}
                            onchange={(e) => {
                              const sanitizedValue = isNaN(Number(e.target.value)) || e.target.value === ""
                                ? ""
                                : Number(e.target.value);
                              if (typeof sanitizedValue === "number" && sanitizedValue < 0) {
                                showErrorToast("Pre Order Alert Minutes must be a positive number");
                                return;
                              }
                              setFormState((prev) => ({
                                ...prev,
                                preOrderAlertInMins: sanitizedValue,
                              }));
                            }}
                            placeHolder="Enter minutes for pre-order alert"
                            name="preOrderAlertInMins"
                          />
                        </div>
                      )}
                      <div className="list-group-item bg-light border-top">
                        <div className="d-flex justify-content-end gap-2 mt-3">
                          <button
                            className="btn settings-btn settings-btn--ghost"
                            onClick={cancelPreOrderingEditing}
                            disabled={loading}
                          >
                            <i className="fas fa-times me-1"></i>
                            Cancel
                          </button>
                          <button
                            className="btn settings-btn settings-btn--primary"
                            style={{
                              backgroundColor: themeColor,
                              color: 'white',
                              border: 'none',
                              transition: 'all 0.3s ease'
                            }}
                            onClick={savePreOrderingSettings}
                            disabled={loading}
                          >
                            {loading ? (
                              <>
                                <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                                Saving...
                              </>
                            ) : (
                              <>
                                <i className="fas fa-save me-1"></i>
                                Save Changes
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
            )}
            {activeSettingsTab === "printingReceipts" && (
              <div className="settings-tab-panel">
                {renderSectionHeading(
                  "Printing Receipts",
                  "Receipt templates and printer-related options for your outlets."
                )}
                <PrintingReceipts embedInSettings />
              </div>
            )}
          </div>
        </div>
        </GridSectionLoader>
      </div>

      <PaymentMethodModal
        isModalOpen={isPaymentModalOpen}
        setIsModalOpen={setIsPaymentModalOpen}
        onPaymentMethodSubmit={handlePaymentMethodSubmit}
        compID={CompID}
      />

      <SubscriptionModal
        isModalOpen={isSubscriptionModalOpen}
        setIsModalOpen={setIsSubscriptionModalOpen}
        onSubscriptionSubmit={handleSubscriptionSubmit}
        compID={CompID}
      />
    </>
  );
}

export default Settings;
